import type { ObjectReadRequest, ObjectReadResult } from "./objectRead";
/**
 * Tiny fetch wrapper. The base URL is exposed by Vite via env.
 * In dev with NGINX in front, leaving it relative to /api also works.
 */
import type { ChannelCorrespondence, ChannelMapEntry } from "./compareColors";
import {
  clearSessionTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "./auth";
import { mensagemDo401, recoverFrom401 } from "./sessionExpiry";
import { comCapability, type UrlCapability } from "./capability";
import { throwIfMaskPending, type CompareMaskStatus } from "./compareMask";
import { comExtensao, nomeDoDownload } from "./downloadName";
import { mensagemDaFalha } from "./httpError";
import { pedirComEspera, type PaginasEmPreparo } from "./notesReport";
import { STORAGE_STACK_METRICS } from "./insightsStorage";
import type {
  DictionaryAddRequest,
  DictionaryAddResult,
  DictionaryEntry,
  DictionaryLanguage,
  DictionaryList,
  DictionarySkipped,
} from "./spellingDictionary";
import type { ProTrial, ProTrialList } from "./viewerProTrial";

function stripTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

function isLoopbackHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname === "[::1]" ||
    hostname.startsWith("127.")
  );
}

function effectivePort(url: URL): string {
  if (url.port) return url.port;
  return url.protocol === "https:" ? "443" : "80";
}

function isEquivalentHost(target: URL, current: URL): boolean {
  if (target.host === current.host) return true;
  return (
    isLoopbackHostname(target.hostname) &&
    isLoopbackHostname(current.hostname) &&
    effectivePort(target) === effectivePort(current)
  );
}

function resolveApiBase(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  const fallback = "/api";
  if (!raw) return fallback;

  const normalized = stripTrailingSlash(raw);
  if (typeof window === "undefined") return normalized;

  try {
    const url = new URL(normalized, window.location.origin);
    const current = new URL(window.location.origin);
    if (isEquivalentHost(url, current)) {
      // Keep same-origin calls relative to avoid scheme mismatch in local dev.
      return stripTrailingSlash(url.pathname || fallback);
    }
    if (window.location.protocol === "https:" && url.protocol === "http:") {
      url.protocol = "https:";
      return stripTrailingSlash(url.toString());
    }
  } catch {
    // Keep raw value when it's not a valid URL (e.g. custom runtime injection).
  }

  return normalized;
}

const API_BASE = resolveApiBase();

function toSameOriginPath(input: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(input, window.location.origin);
    const current = new URL(window.location.origin);
    if (!isEquivalentHost(url, current)) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export interface FileListItem {
  id: string;
  public_slug: string;
  filename: string;
  pages: number | null;
  size_bytes: number;
  status: string;
  created_at: string;
  viewer_url: string;
  /** Duration of the most recent completed render in seconds. Null if not yet finished. */
  render_seconds: number | null;
}

export type AssetStatus = "ready" | "queued" | "rendering" | "preparing" | "error" | "expired";

/**
 * A retícula declarada no XMP, como o painel da F-T.5 a lê.
 *
 * O tipo mora em `screening.ts` porque é lá que ele é interpretado, e porque
 * aquele módulo roda fora do bundler (`node --test`). Aqui ele só é
 * republicado, para quem consome a API continuar achando tudo em um lugar.
 */
import type { DgcCurve, ScreenSet as ViewerScreenSet } from "./screening";

export type { DgcCurve, ViewerScreenSet };

export interface ViewerV1Profile {
  key: "default" | "isocoated_v2_eci" | string;
  label: string;
}

export interface ViewerV1ChannelMetadata {
  name: string;
  /** O `id` estável do canal (`K`, `spot:31`); as camadas falam por ele. */
  id?: string | null;
  /** Rótulo de apresentação; o render declara `C`, o painel mostra `Cyan`. */
  label?: string;
  /** Slug estável: é por ele que a seleção deveria trafegar. */
  slug?: string;
  kind?: string;
  role?: string | null;
  is_technical?: boolean;
  /** Se o canal começa marcado. Ausente = «não tenho opinião». */
  default_enabled?: boolean;
  /** Nome do contrato da Fase T; espelha `default_enabled` (F-T.3). */
  default_visible?: boolean;
  display?: {
    rgb?: number[] | null;
    opacity?: number;
    blend_mode?: string;
  } | null;
  /** O `egInk:r/g/b` do XMP, em 0–1 — a escala da fórmula de composição. */
  rgb?: number[] | null;
  /** O catálogo de onde a tinta veio (`pms1000c`), verbatim do XMP. */
  ink_book?: string | null;
  /** `egInk:type` verbatim: `process` | `pantone` | `designer` (F-T.1, §10.2). */
  ink_kind?: string | null;
  /**
   * As retículas que **esta separação declara**. Uma lista, sempre: os jobs
   * medidos têm 1, 14 e 39 numa tinta só (`F-T.0-plano.md`, §1).
   */
  screens?: ViewerScreenSet[] | null;
  /** `egInk:type` verbatim, para o bloco "Tinta" do modal (F-T.10, §4). */
  ink_type?: string | null;
  /** `egInk:printingmethod` verbatim: `WSI`, `A00`, `unknown`. */
  printing_method?: string | null;
  /** O nome do arquivo de onde a tinta saiu. Nunca o caminho do contêiner. */
  source_filename?: string | null;
  /**
   * As curvas DGC que este arquivo declara — 256 pontos cada.
   *
   * A série mora aqui **uma vez**; cada retícula aponta para a sua pelo nome,
   * em `screens[].dgc_lw`/`dgc_ct`. Duplicar por retícula daria 39 cópias no
   * 1198351 (`F-T.10`, §4.1).
   */
  dgc_curves?: DgcCurve[] | null;
  /** `egGr:scanlinedirection` verbatim. Dado para o aviso do §19.2, não regra. */
  scanline_direction?: string | null;
}

/** Um membro da pasta que o conjunto recusou, com o motivo estruturado. */
export interface ViewerExcludedMember {
  path?: string | null;
  ink_name?: string | null;
  /** `geometry_mismatch` | `job_mismatch` | `view_variant` — código, não texto. */
  reason?: string | null;
}

export interface ViewerV1Dimensions {
  width?: number;
  height?: number;
  width_px?: number;
  height_px?: number;
  width_pt?: number;
  height_pt?: number;
  width_mm?: number;
  height_mm?: number;
  units?: string;
  measurement_status?: string;
  resolution_source?: string | null;
  embedded_dpi_x?: number | null;
  embedded_dpi_y?: number | null;
}

/** O discriminador do miolo visual. Ausente em payloads antigos = packaging. */
export type ViewerKind = "packaging" | "document";

export interface ViewerDocumentTile {
  x: number;
  y: number;
  width?: number;
  height?: number;
  path?: string;
}

export interface ViewerDocumentRasterLevel {
  level?: number;
  dpi: number;
  width: number;
  height: number;
  tile_size?: number;
  tiles?: ViewerDocumentTile[];
}

export interface ViewerDocumentPage {
  /** Página pública, sempre 1-based no inventário de documentos. */
  page: number;
  width_pt: number;
  height_pt: number;
  rotation: number;
  status?: string;
  /** Falso enquanto a página está preparada mas ainda não rasterizada (F12.7). */
  raster_ready?: boolean;
  text_status?: "native" | "none" | "unsupported" | string;
  preview_url?: string;
  thumbnail_url?: string;
  thumb_url?: string;
  tile_source_url?: string;
  tile_url_template?: string;
  levels?: ViewerDocumentRasterLevel[];
}

export interface ViewerDocumentOutlineItem {
  title: string;
  target_page?: number | null;
  position?: [number, number] | null;
  destination_status?: "resolved" | "unresolved" | "missing" | string;
  children?: ViewerDocumentOutlineItem[];
}

export interface ViewerDocumentBootstrap {
  renderer_version: string;
  source_sha256: string;
  document?: {
    page_count?: number;
    title?: string | null;
    text_status?: string;
    outline?: ViewerDocumentOutlineItem[] | null;
    links?: Record<string, boolean> | null;
  };
  pages: ViewerDocumentPage[];
}

export interface ViewerDocumentSemantics {
  schema_version: string;
  page: number;
  coordinate_space: "document_page_top_left_v1" | string;
  page_box: { width: number; height: number };
  text: { status: "native" | "empty" | "unmapped" | "unsupported" | string; logical_text: string; runs: ViewerDocumentTextRun[] };
  links: ViewerDocumentLink[];
}

export type ViewerDocumentRect = [number, number, number, number];

export interface ViewerDocumentTextRun {
  start: number;
  end: number;
  text: string;
  rects: ViewerDocumentRect[];
  bbox: ViewerDocumentRect | null;
  angle?: number;
}

export interface ViewerDocumentLink {
  kind: "uri" | "mailto" | "goto" | "blocked" | string;
  rects: ViewerDocumentRect[];
  uri?: string;
  target_page?: number;
  position?: [number, number];
  destination_status?: "resolved" | "unresolved" | "missing" | string;
}

export interface ViewerDocumentSearchResult {
  page: number;
  offset: number;
  end: number;
  rects: number[][];
}

export interface ViewerDocumentSearchResponse {
  query: string;
  offset: number;
  limit: number;
  total: number;
  results: ViewerDocumentSearchResult[];
}

/**
 * Diff semântico entre dois documentos (`document_compare_v1`).
 *
 * O payload carrega **offsets**, nunca retângulos: `{page,start,end}` no
 * `logical_text` daquela página, o mesmo índice que a busca e as notas usam.
 * Quem converte para faixas é o cliente, com `documentSelectionRects` — uma
 * régua só para as três camadas.
 *
 * `a` é o lado «antes», `b` é o «depois». Um `delete` não tem o que pintar em
 * `b`: o range vem com `start === end`, marcando a POSIÇÃO onde o texto
 * estava, e é dela que sai o pino de margem.
 */
export interface DocumentCompareRange {
  page: number;
  start: number;
  end: number;
}

export type DocumentCompareOp = "insert" | "delete" | "replace";
export type DocumentCompareStatus =
  | "unchanged"
  | "modified"
  | "added"
  | "removed";

export interface DocumentCompareSpan {
  op: DocumentCompareOp;
  /**
   * A faixa em cada lado. `null` quando não há o que pintar deste lado — é o
   * que permite um trecho de origem responder a VÁRIAS faixas no outro
   * documento sem ser repintado uma vez por faixa (opacidade sobre opacidade
   * escurece a marca e some com o texto embaixo).
   */
  a: DocumentCompareRange | null;
  b: DocumentCompareRange | null;
  tags: string[];
  /** Categoria do achado — só no modo conferência. Ver `documentConference`. */
  cat?: string;
  /** O comentário da revisão, quando há. Vira o balão de passagem do ponteiro. */
  comment?: string;
}

/**
 * A forma de um achado de negrito ou de espaçamento: o texto é o mesmo dos
 * dois lados, e o que difere é isto. Ausente nos achados de texto.
 */
export interface DocumentStyleFields {
  from_bold?: boolean;
  to_bold?: boolean;
  from_spaces?: number;
  to_spaces?: number;
}

export interface DocumentComparePair extends DocumentStyleFields {
  from: string;
  to: string;
  /** Só nos achados de forma (`negrito`, `espacamento`), para a prévia saber o que mostrar. */
  cat?: string;
}

export interface DocumentCompareBlock {
  id: string;
  label: string;
  status: DocumentCompareStatus;
  a: DocumentCompareRange | null;
  b: DocumentCompareRange | null;
  spans: DocumentCompareSpan[];
  pairs: DocumentComparePair[];
  tags: string[];
  /** Resumo opcional da camada de IA — ausente sem o job. */
  summary?: string | null;
  impact?: "alto" | "médio" | "baixo" | string | null;
  /**
   * Só no modo conferência: o bloco é uma SEÇÃO do perfil, e estes campos
   * dizem quanto dela conferiu. `null` na zona que não entra no veredito (o
   * carimbo da gráfica, o anexo regulatório).
   */
  coverage?: number | null;
  chars?: number;
  matched?: number;
  /** Caracteres em zona de tabela, à espera da comparação célula a célula. */
  pending?: number;
  /** Caracteres que divergem de verdade. */
  divergent?: number;
  /** Achados que pesam no veredito — nem reordenação nem tabela. */
  hard?: number;
}

export interface DocumentCompareStats {
  blocks: number;
  unchanged: number;
  modified: number;
  added: number;
  removed: number;
  changed: number;
  spans: number;
  tags: Record<string, number>;
  /** Modo conferência: a fração do documento de referência que conferiu. */
  coverage?: number;
  chars?: number;
  matched?: number;
  pending?: number;
  divergent?: number;
  categories?: Record<string, number>;
}

/** Quanto texto cada lado ofereceu — a triagem de «arte em curvas». */
export interface DocumentConferenceSideText {
  pages: number;
  chars: number;
  chars_per_page: number;
  extractable: boolean;
  furniture: { lines: number; sample: string[] };
}

export interface DocumentCompareSide {
  slug: string;
  filename: string;
  cache_identity: string;
}

export interface DocumentCompare {
  schema_version: string;
  algorithm_version: string;
  stats: DocumentCompareStats;
  blocks: DocumentCompareBlock[];
  truncated: boolean;
  cached: boolean;
  left: DocumentCompareSide;
  right: DocumentCompareSide;
  /** Presente quando o resumo por bloco foi gerado (fase 2). */
  ai?: { status: string; model?: string | null } | null;

  /* --- modo conferência (arte diagramada × especificação) --- */
  mode?: "changes" | "conference";
  /**
   * O perfil de seções pedido e se ele valeu para ESTE par. `applied: false`
   * quer dizer que as âncoras não casaram e os blocos não são as seções do
   * perfil — o painel não pode prometer rótulo que o diff não tem.
   */
  profile?: { name: string; label: string; applied?: boolean };
  /**
   * Qual lado é a arte e qual é a especificação, deduzido da densidade da
   * página. `indefinido` quando a razão é morna — aí a tela fala em «esquerda»
   * e «direita», que é o que ela sabe.
   */
  roles?: { a: string; b: string; reference: string };
  reference?: string;
  text?: { a: DocumentConferenceSideText; b: DocumentConferenceSideText };
  anchors?: { total: number; matched: number; missing_a: string[]; missing_b: string[] };
}

/** Uma caixa de palavra do Vision, em pixels da página base da arte (fase DA). */
export interface ConferenceBox {
  bbox: { x: number; y: number; width: number; height: number };
  polygon: Array<{ x: number; y: number }>;
  confidence?: number | null;
  text_start: number;
  text_end: number;
}

/** Uma faixa do lado da ARTE: os offsets de sempre, mais as caixas para pintar. */
export interface ConferenceArtRange extends DocumentCompareRange {
  boxes?: ConferenceBox[];
  confidence_min?: number | null;
}

/** Um portão que disparou fora do diff de caracteres (número, unidade, negação). */
export interface ConferenceGate {
  kind: "numero" | "unidade" | "negacao" | string;
  selection: string;
  other: string;
}

/** Os cinco estados do veredito (fase DA). `sem_leitura` é do 409 da rota. */
export type ConferenceState =
  | "confere"
  | "confere_com_ressalva"
  | "divergente"
  | "ausente"
  | "sem_leitura";

/** O veredito de um trecho conferido no outro documento. */
export interface DocumentLocateFinding extends DocumentStyleFields {
  cat: string;
  /** `leitura_possivel`: a troca é um par de confusão de OCR (acento, 0/O, 1/l…). */
  tags?: string[];
  /** Onde a divergência está no documento de ORIGEM da seleção. */
  source: DocumentCompareRange;
  /** E onde ela está no outro, em faixas por página. */
  other: ConferenceArtRange[];
  from: string;
  to: string;
  /** A vizinhança de cada lado, para a prévia mostrar a frase e não a palavra solta. */
  from_before?: string;
  from_after?: string;
  to_before?: string;
  to_after?: string;
}

/** Um número da varredura (fase DA/I4): o texto, onde está na seleção e o `locate` dele na arte. */
export interface NumberSweepToken {
  text: string;
  start: number;
  end: number;
  result: DocumentLocateResult;
}

export interface NumberSweepResult {
  selection: DocumentCompareRange;
  tokens: NumberSweepToken[];
  truncated: boolean;
}

export interface DocumentLocateResult {
  status: "confere" | "divergente" | "ausente";
  /** O mesmo veredito em cinco estados (fase DA); ausente em backend anterior. */
  state?: ConferenceState;
  /** Fração da seleção que casou, de 0 a 1. */
  matched: number;
  /** Quantas ocorrências literais a chave tem do outro lado (teto 8). */
  occurrences?: number;
  gates?: ConferenceGate[];
  /** `packaging` quando o outro lado é a arte (as faixas trazem caixas). */
  target_kind?: "document" | "packaging";
  /** De qual lado veio a seleção: `a` é o comparado, `b` é o documento aberto. */
  side: "a" | "b";
  source: DocumentCompareRange;
  text: string;
  /** O trecho correspondente no outro documento. Vazio quando não há. */
  ranges: ConferenceArtRange[];
  findings: DocumentLocateFinding[];
}

/** O 409 `sem_leitura`: a arte ainda não tem texto identificado. */
export interface ConferenceSemLeitura {
  code: "sem_leitura";
  analysis_status: "missing" | "queued" | "running" | "failed" | "empty" | string;
  progress?: number | null;
  message: string;
}

export interface ViewerCapabilities {
  channels: boolean;
  channel_selector: boolean;
  eyedropper: boolean;
  coverage_filter: boolean;
  ruler: boolean;
  notes: boolean;
  barcode: boolean;
  braille: boolean;
  ocr: boolean;
  table_ocr: boolean;
  text_mode: boolean;
  page_info: boolean;
  [key: string]: boolean;
}

export interface ViewerV1Render {
  render_id: string;
  cache_token?: string | null;
  /** Render pedido com «Renderizar com camadas» (só no payload de abertura). */
  optional_content_layers?: boolean;
  /**
   * O job que corta as chapas parciais das camadas ainda está na fila ou
   * rodando: a página já abre, e as camadas ligam quando ele terminar.
   */
  optional_content_pending?: boolean;
  /** Identidade ISO opaca e segura para caminho; muda quando muda a origem dos pixels. */
  materialized_source_id?: string | null;
  asset_id: string;
  asset_version: string;
  status: string;
  ready: boolean;
  page: number;
  requested_dpi_mode: "fixed_600" | "adaptive" | string;
  memory_profile?: string | null;
  effective_dpi: number | null;
  profile: ViewerV1Profile;
  visual_policy: string;
  dimensions?: ViewerV1Dimensions | null;
  channels?: string[];
  channel_metadata?: ViewerV1ChannelMetadata[];
  capabilities?: Partial<ViewerCapabilities>;
  tile_source_url: string;
  preview_url: string;
  thumbnail_url?: string;
  thumb_url?: string;
  timings?: Record<string, unknown>;
  warnings?: string[];
  error?: string | null;
  page_count?: number;
  primary_page?: number;
  primary_cache_id?: string | null;
  pages_status?: string;
  pages?: ViewerV1RenderPage[];
  viewer_kind?: ViewerKind;
  document_v1?: ViewerDocumentBootstrap;
}

export interface ViewerV1Thumbnail {
  status?: string;
  kind?: string;
  content_type?: string;
  format?: string;
  quality?: number;
  max_side?: number | null;
  width?: number;
  height?: number;
  dpi?: number;
  bytes?: number;
  selected_strategy?: string;
  warnings?: string[];
  /** Como o preview: o degrau composto e as tintas que entraram nele. */
  level?: number;
  channels?: string[] | null;
  url?: string;
}

export interface ViewerV1Preview {
  max_side?: number;
  width?: number;
  height?: number;
  /**
   * O descritivo de como a imagem foi composta, no conjunto de separações: em
   * que degrau da pirâmide (`level`), com que codificador e qualidade, e
   * quantos bytes saíram. Diagnóstico, não insumo de layout.
   */
  status?: string;
  kind?: string;
  format?: string;
  quality?: number;
  bytes?: number;
  level?: number;
  content_type?: string;
  url?: string;
  channels?: string[];
  composition_policy?: string;
  composition_order?: string[];
}

export interface ViewerV1RenderPage {
  page: number;
  cache_token?: string | null;
  materialized_source_id?: string | null;
  primary?: boolean;
  status?: string;
  ready?: boolean;
  cache_id?: string | null;
  dpi?: number | null;
  dimensions?: ViewerV1Dimensions | null;
  channels?: string[];
  channel_metadata?: ViewerV1ChannelMetadata[];
  capabilities?: Partial<ViewerCapabilities>;
  preview_url?: string;
  preview?: ViewerV1Preview;
  thumbnail_url?: string;
  thumb_url?: string;
  thumbnail?: ViewerV1Thumbnail;
  tile_source_url?: string;
  warnings?: string[];
  error?: string | null;
  levels?: ViewerDocumentRasterLevel[];
  tile_url_template?: string;
}

export interface ViewerV1TileSourceLevel {
  id?: string;
  dpi: number;
  /**
   * A numeração do degrau, nos dois sentidos, e a densidade física exata.
   *
   * `level` conta 0 = nativo, que é o sentido do contrato; `z` é o do
   * `dzsave`, que numera ao contrário. Os dois vão publicados para ninguém
   * precisar reconstruir a inversão do lado de fora — trocar um pelo outro
   * serve o nível errado em silêncio. `dpi` é o rótulo que a URL transporta;
   * `dpi_exact` é o número físico, e é dele — nunca do rótulo — que régua e
   * medição saem. Só no conjunto de separações.
   */
  level?: number;
  z?: number;
  dpi_exact?: number | null;
  /** Redução deste degrau em relação ao nativo (1, 0,5, 0,25…). */
  scale?: number;
  width: number;
  height: number;
  tile_size: number;
  columns: number;
  rows: number;
  ready: boolean;
  /**
   * O formato do tile **deste nível**, e a extensão com que ele é endereçado.
   *
   * No conjunto de separações o formato muda com o nível: WebP sem perda no
   * nativo e no 1:2, onde a retícula ainda está resolvida, e JPEG do 1:4 para
   * baixo, onde ele é mais rápido *e* menor (F-T.11, §3). Em raster e PDF os
   * campos não vêm, e a extensão continua sendo `.webp` — que é o valor que
   * `tileExtensionForDpi` devolve quando eles faltam.
   *
   * Enquanto isto não existia, o cliente montava `…/{x}_{y}.webp` na mão.
   */
  format?: string;
  extension?: string;
  content_type?: string;
  /** O document renderer publica a grade real; packaging continua sem lista. */
  tiles?: ViewerDocumentTile[];
}

export interface ViewerV1ZoomPolicyEntry {
  viewer_zoom: [number, number];
  source: "preview" | "tile" | string;
  dpi: number;
}

export interface ViewerV1TileSource {
  schema_version: string;
  /**
   * As camadas OCG da página (rflow 2.4.x), como o daemon as publica. `null`
   * em página sem camadas e em todo render anterior ao «Renderizar com camadas».
   */
  optional_content?: import("./optionalContent").OptionalContentBlock | null;
  render_id: string;
  source_type?: "pdf" | "raster" | string;
  cache_token?: string | null;
  materialized_source_id?: string | null;
  page?: number;
  primary?: boolean;
  status: string;
  ready: boolean;
  base_dpi: number;
  base_dimensions: ViewerV1Dimensions;
  /**
   * A URL é garantida; o **tamanho não**.
   *
   * `max_side` já foi obrigatório aqui, e o tipo estava mentindo: um render
   * cuja entrada de página não projeta preview chega com `{ url }` e mais
   * nada. O que a obrigatoriedade produzia não era o campo — era um
   * `?? Math.max(largura, altura)` no cliente, isto é, o tamanho da **arte**
   * ocupando o lugar do tamanho do preview. Quem decide degrau de pirâmide por
   * resolução acreditava, e concluía que um preview de 1083 px tinha 4331.
   */
  preview: ViewerV1Preview & { url: string };
  thumbnail?: ViewerV1Thumbnail;
  thumbnail_url?: string;
  thumb_url?: string;
  tile_size: number;
  /**
   * O formato dos tiles **por tinta** — não o do tile composto que o cliente
   * busca, que é do nível (ver `ViewerV1TileSourceLevel.format`). Eles são sem
   * perda em todos os degraus porque é deles que a medição de retícula lê o
   * ponto. Só no conjunto de separações.
   */
  tile_format?: string;
  tile_lossless?: boolean;
  tile_url_template?: string;
  zoom_policy: ViewerV1ZoomPolicyEntry[];
  /**
   * Escala máxima útil no nível 0 — o que o cliente usa para derivar o teto de
   * zoom (`zoomLimits` em `viewerShared.ts`). 2,0 no conjunto de separações,
   * 1,0 em raster e PDF. Ausente em render anterior ao campo: vale 1,0, e a
   * régua histórica de 1200 % continua.
   */
  max_scale?: number;
  levels: ViewerV1TileSourceLevel[];
  channels: string[];
  channel_metadata?: ViewerV1ChannelMetadata[];
  /** Só no conjunto de separações: quem ficou de fora, e por quê. */
  excluded_members?: ViewerExcludedMember[];
  /**
   * O conjunto foi montado com o que havia na pasta.
   *
   * É o caso **normal**: o Esko não manda manifesto, então só quem chama a API
   * por pasta declarando a contagem consegue afirmar que o job está completo.
   */
  partial?: boolean;
  declared_inks?: number | null;
  /**
   * A identidade da peça, como o servidor a calculou: o sha256 das tintas
   * efetivas mais os campos do job no XMP do Esko (`egJob:orderid`,
   * `suborderid`) e como o conjunto foi agrupado. Só no conjunto de
   * separações; é o que faz sete arquivos apontarem para um render só.
   */
  identity_sha256?: string | null;
  order_id?: string | null;
  suborder_id?: string | null;
  grouping?: string | null;
  pixel_format?: "rgb" | "rgba" | string;
  transparent_background?: boolean;
  capabilities?: Partial<ViewerCapabilities>;
  cache_id?: string | null;
  page_count?: number;
  pages?: ViewerV1TileSourcePage[];
}

export type ViewerV1TileSourcePage = Omit<ViewerV1TileSource, "pages">;

export interface ViewerV1ViewInfoDimensions {
  width?: number;
  height?: number;
  width_px?: number;
  height_px?: number;
  width_pt?: number;
  height_pt?: number;
  width_mm?: number;
  height_mm?: number;
}

export interface ViewerV1ViewInfoChannel {
  name: string;
  id?: string | null;
  label?: string;
  slug?: string;
  kind?: string;
  role?: string | null;
  role_source?: string | null;
  role_confidence?: number | null;
  is_technical?: boolean;
  default_enabled?: boolean;
  display?: {
    rgb?: number[] | null;
    opacity?: number;
    blend_mode?: string;
  } | null;
  color?: Record<string, unknown> | null;
  mask?: Record<string, unknown> | null;
  /** Espelha `ViewerV1ChannelMetadata.screens` — ver `separation_view_info`. */
  screens?: ViewerScreenSet[] | null;
  /**
   * O detalhe da tinta que o modal mostra (F-T.10, §4).
   *
   * Espelha `ViewerV1ChannelMetadata` de propósito: o viewer deriva o
   * tile-source **do view-info** quando ele existe, e um campo que só mora do
   * outro lado morre na conversão. Já aconteceu com `max_scale`, `partial`,
   * `declared_inks` e o nome da peça.
   */
  ink_kind?: string | null;
  ink_type?: string | null;
  ink_book?: string | null;
  printing_method?: string | null;
  source_filename?: string | null;
  dgc_curves?: DgcCurve[] | null;
  scanline_direction?: string | null;
}

export type ViewerV1ViewInfoPreview = ViewerV1Preview;

export interface ViewerV1ViewInfoTiles {
  format?: string;
  /** Espelha `ViewerV1TileSource.tile_lossless` — os tiles por tinta. */
  lossless?: boolean;
  tile_size: number;
  base_dpi: number;
  base_dimensions?: ViewerV1ViewInfoDimensions;
  levels: ViewerV1TileSourceLevel[];
  url_template?: string;
  /** Espelha `ViewerV1TileSource.max_scale`. */
  max_scale?: number;
}

export interface ViewerV1ViewInfo {
  schema: "viewer_view_info_v1" | string;
  /**
   * As camadas OCG da página (rflow 2.4.x), como o daemon as publica. `null`
   * em página sem camadas e em todo render anterior ao «Renderizar com camadas».
   */
  optional_content?: import("./optionalContent").OptionalContentBlock | null;
  /** Presente desde a Fase 5; ausente em renders antigos. */
  source_type?: "pdf" | "raster" | string;
  render_id: string;
  cache_token?: string | null;
  materialized_source_id?: string | null;
  asset_id?: string;
  asset_version?: string;
  status: string;
  ready: boolean;
  page: number;
  dpi_mode: "fixed_600" | "adaptive" | string;
  requested_dpi_mode?: "fixed_600" | "adaptive" | string;
  effective_dpi: number | null;
  visual_policy?: string;
  dimensions?: ViewerV1ViewInfoDimensions | null;
  channels: ViewerV1ViewInfoChannel[];
  capabilities?: Partial<ViewerCapabilities>;
  preview: ViewerV1ViewInfoPreview;
  thumbnail?: ViewerV1Thumbnail;
  thumbnail_url?: string;
  thumb_url?: string;
  tiles: ViewerV1ViewInfoTiles;
  zoom_table: ViewerV1ZoomPolicyEntry[];
  /**
   * O mesmo `max_scale` de `tiles`, no topo.
   *
   * O servidor publica nos dois lugares e o cliente lê o de `tiles` ao derivar
   * o tile-source. Estar declarado aqui é o que impede a repetição do defeito
   * que fez o campo morrer duas vezes na travessia (plano da Fase T, §17.3).
   */
  max_scale?: number;
  ready_levels: number[];
  /** Espelham o tile-source do conjunto — o viewer entra por aqui (F-T.5). */
  excluded_members?: ViewerExcludedMember[];
  partial?: boolean;
  declared_inks?: number | null;
  warnings?: string[];
  page_count?: number;
  primary?: boolean;
  pages?: ViewerV1ViewInfoPage[];
}

export type ViewerV1ViewInfoPage = Omit<ViewerV1ViewInfo, "pages">;

export interface ViewerV1AssetVersion {
  id: string;
  sha256: string;
  source_uri: string;
  source_type?: "pdf" | "raster" | string;
  source_mime?: string;
  source_ext?: string;
  file_size: number;
  page_count: number | null;
  metadata_status: string;
  metadata: Record<string, unknown> | null;
  error_message: string | null;
}

/**
 * A identidade da peça, quando o arquivo aberto é uma separação do Esko (F-T.9).
 *
 * Vem do backend com a **pasta**, e não com o nome pronto: o rótulo é do
 * cliente, que já tem o `folderName()`. O que o servidor não pode delegar é o
 * `filename_pattern`, que depende de conhecer os membros efetivos do conjunto.
 */
/**
 * As duas leituras de uma peça de separações (F-T.14): `len`, as chapas de
 * gravação, e `view`, a simulação do impresso que vem numa subpasta `view`.
 */
export type SeparationVariant = "len" | "view";

export interface ViewerV1SeparationSet {
  slug: string;
  folder_path: string;
  filename_pattern: string;
  anchor_filename: string;
  anchor_asset_id: string;
  ink_count: number;
  declared_inks: number | null;
  /** `["len", "view"]` quando a pasta tem VIEW; nulo ou ausente sem ela. */
  variants?: SeparationVariant[] | null;
}

/** A empresa do LinkFlow dona da arte (a do último link emitido para ela). */
export interface ArtOrganization {
  id: string;
  name: string | null;
}

export interface ViewerV1BySlug {
  /**
   * Os bytes deste arquivo saíram do viewer (F-GC.4): pirâmide e original. O
   * registro ficou — notas, códigos, miniatura —, mas não há o que desenhar até
   * a plataforma reenviar. Com `true` não vêm `tile_source` nem `view_info`, e
   * a tela mostra «reenvie pela plataforma» sem pedir imagem nenhuma. Ausente
   * em backend anterior à F-GC.4, o que vale `false`.
   */
  expired?: boolean;
  /** Quando os bytes saíram (ISO 8601); nulo em arquivo vivo. */
  expired_at?: string | null;
  viewer_kind?: ViewerKind;
  /** Inventário disponível quando o render de documento já está pronto. */
  document?: ViewerDocumentBootstrap | null;
  /**
   * O link da peça. Igual ao `file.public_slug` fora do conjunto de
   * separações; dentro dele é o slug do conjunto, e a tela troca a barra de
   * endereço por ele — o operador precisa ver o link que vai copiar.
   */
  canonical_slug?: string | null;
  separation_set?: ViewerV1SeparationSet | null;
  /**
   * A leitura servida de fato (F-T.14). `view` só quando a peça tem VIEW e ela
   * foi pedida; ausente em backend anterior, o que vale `len`.
   */
  variant?: SeparationVariant;
  file: {
    id: string;
    public_slug: string;
    filename: string;
    sha256: string;
    size_bytes: number;
    pages: number | null;
    status: string;
    error_message: string | null;
    asset_id: string | null;
  };
  asset_version: ViewerV1AssetVersion | null;
  /**
   * Quanto o ProofScope levou para renderizar este mesmo arquivo, enquanto os
   * dois sistemas convivem. Ausente quando o arquivo não existe lá ou ainda não
   * foi medido. Some quando a ponte de notas sair.
   */
  proofscope?: ViewerV1ProofscopeRender | null;
  render: ViewerV1Render | null;
  job: {
    job_id: string;
    status: string;
    progress: Record<string, unknown>;
    error: string | null;
  } | null;
  tile_source: ViewerV1TileSource | null;
  view_info?: ViewerV1ViewInfo | null;
  /**
   * A capability da URL (F-S6): o `k` que o cliente anexa às URLs que monta
   * sozinho, e quando renová-lo. `null` quando não há versão a autorizar;
   * ausente num backend anterior à F-S6.
   */
  url_capability?: UrlCapability | null;
  /**
   * A empresa da arte (21/10/2026), só para administrador de sessão: é onde o
   * «aceitar» do gerenciador pode gravar além do global. Nula sem empresa.
   */
  organization?: ArtOrganization | null;
  /**
   * Códigos lidos automaticamente (Fase 8), indexados por página.
   * `null` = render ainda não varrido; `pages: {}` = varrido e nada achado.
   */
  barcodes?: ViewerV1Barcodes | null;
  capabilities?: Partial<ViewerCapabilities>;
}

export interface ViewerV1ProofscopeRender {
  /** Segundos entre `kRendererStarted` e `kRendererFinished` no ProofScope. */
  render_seconds: number;
  started_at: string | null;
  finished_at: string | null;
  collected_at: string | null;
}

export interface ViewerV1DetectedBarcode {
  type: string;
  value: string;
  bbox: [number, number, number, number];
  check_digit_valid: boolean | null;
  /** Dígitos impressos sob as barras e o veredito da conferência (F8R.5). */
  hrt?: string;
  hrt_status?: "confere" | "diverge" | "ilegivel";
}

/**
 * Diagnóstico por página da varredura (Fase 8R, R2).
 *
 * `candidates_failed` NÃO é sinal de leitura parcial — medido no acervo, 356
 * de 383 candidatos ficam sem código em 27 de 27 páginas, porque o localizador
 * de textura é generoso por desenho. Quem responde "a leitura foi parcial?" é
 * `truncated_pages` e o par `safety_net_windows` / `safety_net_total_windows`.
 */
export interface ViewerV1BarcodeDiagnostics {
  candidates?: number;
  candidates_decoded?: number;
  candidates_failed?: number;
  safety_net_windows?: number;
  safety_net_total_windows?: number;
  scanned_windows?: number;
  elapsed_s?: number;
}

export interface ViewerV1Barcodes {
  version: string | null;
  pages: Record<string, ViewerV1DetectedBarcode[]>;
  truncated_pages: number[];
  /** Indexado por número de página, como `pages`. Pode faltar em resultados
   *  gravados antes da v6 — ausência nunca significa "leitura parcial". */
  diagnostics?: Record<string, ViewerV1BarcodeDiagnostics>;
}

/** Os planos do link público, em ordem: cada um inclui o anterior (set/2026). */
export type PlanName = "starter" | "pro" | "enterprise";
/**
 * O que o plano e o `tools` fizeram com uma chave (set/2026).
 *
 * `available` desenha e funciona; `locked` desenha opaco com o selo do plano
 * exigido, e a rota responde 403; `hidden` não desenha — foi o LinkFlow que
 * escondeu, ou a chave é opt-in e não foi ligada.
 */
export type ToolAccessState = "available" | "locked" | "hidden";
export interface ToolAccess {
  state: ToolAccessState;
  required_plan?: PlanName;
}
export type ToolAccessMap = Partial<Record<keyof PublicViewTools, ToolAccess>>;

export interface PublicViewTools {
  channel_selector: boolean;
  eyedropper: boolean;
  coverage_filter: boolean;
  /** O leitor «Ler Código» sob demanda. */
  barcode: boolean;
  /**
   * O painel «Código de barras e QR» com a varredura automática da página.
   *
   * Separada de `barcode` em 13/09: apontar uma área e ler é o plano de
   * entrada; receber a página inteira lida, agrupada e conferida contra o HRI
   * é o que se cobra. Ausente num link anterior à separação, herda `barcode`.
   */
  barcode_auto: boolean;
  braille: boolean;
  ocr: boolean;
  table_ocr: boolean;
  text_mode: boolean;
  page_info: boolean;
  /**
   * **Ver** as notas — separado de `notes_create`, que é **escrever** (F-S2).
   *
   * O botão "Comentários" era renderizado incondicionalmente: `notes_create`
   * decidia só o título e os sub-botões de criação, então nenhum valor de
   * `toolAvailability` o escondia. Um anônimo no compare via o botão e recebia
   * "Session expired" ao clicar.
   */
  notes: boolean;
  notes_create: boolean;
  /**
   * «Exportar relatório» no painel de comentários (set/2026): o PDF com a
   * ficha do link, a arte de cada página com as anotações numeradas e a lista
   * delas. Existe do plano Pro para cima; nasce ligada, como as outras — o
   * relatório não entrega o original, a arte vai rasterizada.
   */
  notes_report: boolean;
  /**
   * Encaixe automático do par (set/2026) — chave do link de comparação.
   *
   * Ligado, a primeira abertura alinha sozinha e a barra oferece «Auto» e
   * «Por objeto»; desligado, o par abre como está e sobra o encaixe por
   * referência. Um link gravado antes da chave não a tem, e ausência é
   * «ligado»: nenhum link em uso muda de comportamento no deploy.
   */
  alignment_auto: boolean;
  /**
   * O painel «Diferenças encontradas» do par (set/2026) — chave do link de
   * comparação.
   *
   * Ligada (o padrão, e o que todo link já emitido faz), o par mostra a lista
   * de regiões à direita, com «Identificar diferenças» e a granulometria.
   * Desligada, o painel não é montado: sobra a arte, os modos de visualização
   * e a máscara do Delta — a comparação continua sendo comparação, só não
   * enumera achados para o convidado.
   */
  differences: boolean;
  /**
   * Baixar o original pelo link (set/2026) — a única chave que nasce
   * **desligada**: um link gravado antes dela nunca ofereceu download, e
   * ausência tem de continuar «não». Ligada, o painel «Informações» mostra o
   * botão e `GET /api/public-views/{token}/download` serve o arquivo.
   */
  download: boolean;
  /**
   * Medição de retícula (F-T.6) — só existe no conjunto de separações.
   *
   * Com sessão quem liga é o viewer, pela rota do render. No link público é
   * chave desde 29/10/2026, em todo plano (no TIFF não há Pro), e a medição vai
   * por `/public-views/{token}/tools/screen-measure`. Ausente em backend anterior.
   */
  screen_measure: boolean;
  /**
   * A seção «Retícula» do Painel Técnico do conjunto TIFF (29/10/2026): o que
   * o XMP de cada tinta declara. Chave do link, em todo plano; ausente é «ligado».
   */
  screening: boolean;
  /**
   * «Medir Texto» (F-MT.1): com sessão, sempre; no link de viewer, chave do
   * Pro desde 21/10/2026 (de 15 a 21/10 foi só do gerenciador).
   */
  text_measure: boolean;
  /**
   * A moldura de comparação (F-CA.5) no link do par: chave do Pro desde
   * 28/10/2026 (antes, só do gerenciador). Com sessão quem decide é
   * `managerToolAllowed`; o mapa só a carrega por ser o da régua.
   */
  compare_frames: boolean;
  /**
   * O «Traduzir» do balão do Ler Texto (TR1) no link: chave do Pro desde
   * 28/10/2026, nos dois links (viewer e par). Com sessão quem decide é
   * `managerToolAllowed`.
   */
  ocr_translation: boolean;
  /**
   * O «aceitar» do dicionário pelo link (21/10/2026): a palavra vai para o
   * dicionário da empresa do link, nunca para o global. Do plano Pro desde
   * 28/10/2026 — até ali opt-in, ligada pelo LinkFlow. Ausente em backend anterior.
   */
  dictionary?: boolean;
}

// ---------- Medir Texto (F-MT.1) ----------

/** De onde saiu a altura: medida nas letras da página, do contorno da fonte, ou de lugar nenhum. */
export type TextMeasureHeightSource = "measured" | "outline" | null;

/** Uma fonte da página, como o serviço a descreve. */
export interface TextMeasureFont {
  /** Nome PostScript, sem o prefixo de subset. */
  ps_name: string;
  /** Família lida da fonte carregada; igual ao nome quando não há outra. */
  family: string;
  subset: boolean;
  /** `false`: as alturas medidas são da fonte substituta. `null`: o PDFium não soube. */
  embedded: boolean | null;
  weight: number | null;
  italic: boolean;
  /** Altura de x ÷ corpo (fração do em). */
  x_height_em: number | null;
  cap_height_em: number | null;
  x_source: TextMeasureHeightSource;
  cap_source: TextMeasureHeightSource;
  x_samples: number;
  cap_samples: number;
}

/** Uma palavra do texto vivo, com UMA fonte e UM corpo. */
export interface TextMeasureWord {
  text: string;
  /** `[x, y, w, h]` em px da base do render. */
  bbox: [number, number, number, number];
  /** Retângulo orientado da palavra em px da base, no sentido da escrita. */
  polygon: [number, number][];
  /** Direção da escrita na imagem, em graus (0 = da esquerda para a direita). */
  angle: number;
  /** Chave em `fonts`. */
  font: string;
  /** Corpo efetivo em pt: Tf × escala vertical da matriz do texto, nunca o Tf. */
  size_pt: number;
  /** Escala horizontal ÷ vertical da matriz: 1,0 é texto sem condensar. */
  h_scale: number;
  fill: [number, number, number] | null;
  unicode_error: boolean;
  x_height_mm: number | null;
  cap_height_mm: number | null;
  order: number;
}

export type TextMeasureWordsStatus = "ok" | "no_pdf" | "no_page_box" | "no_live_text";

/** As palavras do texto vivo da página — ou o motivo de não haver. Os quatro `status` chegam com 200. */
export interface TextMeasureWordsResult {
  version: number;
  status: TextMeasureWordsStatus;
  page: number;
  base: { width_px: number; height_px: number; dpi: number | null };
  box: string | null;
  chars: number;
  unicode_errors: number;
  fonts: Record<string, TextMeasureFont>;
  words: TextMeasureWord[];
}

/** Um caminho do PDF que parece glifo (F-MT.2): a caixa exata do caminho, em px da base. */
export interface TextMeasureGlyph {
  /** `[x, y, w, h]` em px da base, com decimais: a caixa do caminho, sem erro de pixel. */
  bbox: [number, number, number, number];
  /** Altura da caixa em mm, na página como ela aparece. */
  height_mm: number;
  width_mm: number;
  segments: number;
  /** Índice em `lines`; `null` para o glifo solto. */
  line: number | null;
}

/** Glifos vizinhos na mesma base, com os dois patamares de altura. */
export interface TextMeasureLine {
  bbox: [number, number, number, number];
  n: number;
  /** Altura de x: a altura modal dos glifos baixos, em mm. */
  x_mm: number;
  /** Maiúscula/ascendente: a altura modal dos glifos altos; `null` com um patamar só. */
  cap_mm: number | null;
  /** Em pé (0°) ou deitada (90°). */
  orientation: TextMeasureOrientation;
}

export type TextMeasureGlyphsStatus = "ok" | "no_pdf" | "no_page_box" | "no_glyphs";

/** Os glifos em curva da página — ou o motivo de não haver. Os quatro `status` chegam com 200. */
export interface TextMeasureGlyphsResult {
  version: number;
  status: TextMeasureGlyphsStatus;
  page: number;
  base: { width_px: number; height_px: number; dpi: number | null };
  box: string | null;
  /** Caminhos percorridos na página, glifo ou não. */
  paths: number;
  glyphs: TextMeasureGlyph[];
  lines: TextMeasureLine[];
}

/** O texto está em pé (altura da letra no eixo y da base) ou deitado (no eixo x). */
export type TextMeasureOrientation = "upright" | "sideways";

/**
 * Os dois patamares da tinta (F-MT.2), em px da base, relativos à base das
 * letras: cada letra é uma componente conectada; a base é a moda dos fundos,
 * o topo do x a moda dos topos, e as altas o topo mais alto acima disso.
 */
export interface TextMeasureInkLevels {
  /** Da base ao topo do x. */
  x_px: number;
  /** Da base ao topo mais alto das letras altas (maiúscula ou ascendente); `null` com um patamar só. */
  high_px: number | null;
  /** Um patamar só: caixa alta, ou só minúsculas sem haste. */
  single: boolean;
  /** Componentes grandes e inteiras (letras) que entraram na conta. */
  letters: number;
  /** Letras encostadas na borda da seleção, cortadas por ela: não votam (v3). Ausente em respostas antigas. */
  cut?: number;
  /** Todas as letras encostam na borda: a conta usou-as mesmo assim e a medida pode estar cortada (v3). */
  tight?: boolean;
}

/** A caixa da tinta na seleção, em px da base, com a borda em 50 % de cobertura. */
export interface TextMeasureInkResult {
  version: number;
  status: "ok" | "no_ink";
  /** `[x, y, w, h]` da tinta em px da base; `null` sem tinta. */
  bbox: [number, number, number, number] | null;
  pixels: number;
  /** A cor do fundo encontrada (mediana da borda), RGB. */
  background: [number, number, number];
  threshold: number;
  /** px da base por px do recorte. */
  crop_scale: number;
  /** Os patamares da tinta (F-MT.2); `null` sem tinta ou sem letra grande. Ausente em respostas da versão 1. */
  levels?: TextMeasureInkLevels | null;
}

// ---------- Medir Texto: a fonte provável do texto em curva (F-MT.4) ----------

/** Uma face candidata, pelo casamento dos contornos dos glifos com os das fontes do servidor. */
export interface TextMeasureFontCandidate {
  /** Nome PostScript da face (sem prefixo de subset na colheita). */
  postscript: string;
  /** O nome para a tela, pronto («Arial Bold»): em inglês, derivado do PostScript quando a tabela `name` não ajuda. */
  display?: string;
  /** Família da tabela `name`; na colheita, o próprio nome. */
  family: string;
  /** Estilo (Bold, Italic…); vazio na colheita. */
  style: string;
  /** A média do cosseno dos glifos que votaram: ≥ 0,998 é a fonte; 0,99–0,998 parece. */
  score: number;
  /** O corpo implícito em pt: altura do caminho ÷ altura em em do glifo casado. */
  pt: number;
  /** A escala horizontal implícita; 1,0 é texto sem condensar. */
  scale_x: number;
  /** Dispersão da escala por glifo: ≈ 0 na fonte certa, 0,03–0,2 num sósia. */
  scale_spread?: number;
  /** Percentil 25 do cosseno por glifo. */
  score_p25?: number;
  /** As fontes do servidor, ou a colheita das fontes embutidas nos PDFs (F-MT.5). */
  source: "acervo" | "colheita";
}

/** A fonte gravada no PDF sob a seleção (texto vivo), como `GET /text-measure/words` a descreve. */
export interface TextMeasureLiveFont {
  ps_name: string;
  family: string;
  size_pt: number;
  h_scale: number;
  weight: number | null;
  italic: boolean;
  embedded: boolean | null;
  /** As palavras dessa fonte dentro da seleção. */
  text: string;
}

/**
 * `ok`: a fonte está no acervo (score ≥ 0,998); `similar`: parente de desenho
 * (0,99–0,998); `unknown`: fonte não reconhecida (a mais parecida só em `candidates`); `trivial`: forma que
 * casa com qualquer fonte (barra, pontilhado, poucas letras distintas, corpo
 * incoerente); `no_index`: o servidor não tem fonte nenhuma; `live`: a seleção
 * cobre texto vivo; `none`: nada sob a seleção; `no_pdf`; `no_page_box`.
 */
export type TextMeasureFontStatus =
  | "ok"
  | "similar"
  | "unknown"
  | "trivial"
  | "no_index"
  | "live"
  | "none"
  | "no_pdf"
  | "no_page_box";

/** A fonte provável — ou o motivo de não haver. Todos os `status` chegam com 200. */
export interface TextMeasureFontResult {
  version: number;
  status: TextMeasureFontStatus;
  page: number;
  orientation: TextMeasureOrientation;
  /** Glifos que votaram no casamento. */
  glyphs: number;
  /** Os caracteres implícitos na ordem da escrita, para conferência; não é OCR. */
  text: string | null;
  /** Até 3, o melhor primeiro. */
  candidates: TextMeasureFontCandidate[];
  live: TextMeasureLiveFont | null;
}

/** O que a rota recebe: os índices dos glifos de `GET /glyphs` (com a orientação da linha), ou a seleção em px da base. */
export type TextMeasureFontQuery =
  | { glyphs: number[]; orientation: TextMeasureOrientation }
  | { bbox: [number, number, number, number] };

/**
 * A ficha do trabalho que viaja com o link (set/2026): o que o LinkFlow
 * desenhava ao lado do iframe. Cópia tirada na emissão — editar no LinkFlow
 * depois não muda o link. `observations_html` já passou pela lista branca do
 * servidor, e é o único HTML que a tela renderiza como HTML.
 */
export interface PublicViewInfo {
  description?: string | null;
  client_code?: string | null;
  reference_code?: string | null;
  observations_html?: string | null;
}

/**
 * O teste grátis do Viewer Pro da empresa do link (out/2026).
 *
 * `available` é a oferta — `days` é o prazo que o botão anuncia; `active` e
 * `ended` são o teste da empresa, com começo e fim. Nulo (ou ausente, em
 * backend anterior) é «nada a mostrar»: sem empresa no link, oferta desligada
 * ou plano que não é Starter.
 */
export interface PublicViewProTrial {
  state: "available" | "active" | "ended";
  days: number;
  started_at: string | null;
  ends_at: string | null;
  /** A empresa do link — o teste é dela inteira. Nulo se a integração não mandou o nome. */
  organization_name?: string | null;
}

/**
 * O bootstrap da view pública. É o mesmo payload do `by-slug`, **menos** o que
 * a F-S2 tirou de lá.
 *
 * `file.public_slug` e `asset_version.source_uri` deixaram de sair no caminho
 * do token (T-38 e T-47): o slug era o que convertia um token de uma hora em
 * acesso permanente pelo `/v/{slug}`, e o `source_uri` é o endereço de
 * armazenamento do original. O tipo herdava os dois como **obrigatórios** —
 * sem este `Omit` ele continuaria prometendo campos que o servidor não manda
 * mais, que é a pior forma de tipo: a que mente.
 */
export interface PublicViewByToken
  extends Omit<ViewerV1BySlug, "file" | "asset_version"> {
  /**
   * Só chega como `"compare"` quando o token é de um PAR aberto em
   * `/public-view/{token}` por engano: aí o resto do payload é o de
   * `PublicCompareSession`, e a tela redireciona para `/public-compare/`.
   */
  kind?: "compare";
  file: Omit<ViewerV1BySlug["file"], "public_slug">;
  asset_version: Omit<ViewerV1AssetVersion, "source_uri"> | null;
  /**
   * As leituras da peça no link (F-T.14). O link não leva o bloco
   * `separation_set`, que traz o slug da peça (T-38): só isto.
   */
  separation_variants?: SeparationVariant[] | null;
  public_view: {
    id: string;
    /** `viewer` (um arquivo) ou `compare` (um par). Ausente em backend anterior. */
    kind?: "viewer" | "compare";
    expires_at: string;
    tools: PublicViewTools;
    requester_name: string;
    requester_email: string | null;
    /** A ficha do link; nula (ou ausente, em backend anterior) sem ficha. */
    info?: PublicViewInfo | null;
    /** O plano do link (set/2026): o teto do que o convidado vê. Nulo ou ausente é «sem plano». */
    plan?: PlanName | null;
    /**
     * Por chave, disponível, travada (com o plano exigido) ou escondida.
     * Ausente em backend anterior — e aí nada trava. `tools` já vem efetivo:
     * este mapa só acrescenta o selo.
     */
    tool_access?: ToolAccessMap;
    /** O teste grátis do Viewer Pro (out/2026); durante ele, `plan` já vem `pro`. */
    pro_trial?: PublicViewProTrial | null;
    /**
     * A empresa do link (21/10/2026): só o nome, para o «aceitar» dizer em que
     * dicionário grava. Nula no link sem empresa; ausente em backend anterior.
     */
    organization?: { name: string | null } | null;
  };
}

/**
 * A sessão de comparação de uma view pública de PAR (`/public-compare/{token}`).
 *
 * É o `ViewerV1CompareSession` com o token no lugar dos slugs: **sem**
 * `public_slug` nos dois lados (T-38, o mesmo motivo do unitário), e com o
 * bloco `public_view` — a régua de ferramentas e a autoria das notas — que a
 * tela do viewer já conhece.
 */
export interface PublicCompareSession extends ViewerV1CompareSession {
  kind: "compare";
  public_view: PublicViewByToken["public_view"] & { kind: "compare" };
  /**
   * As páginas em que o link abre, gravadas na emissão — e o motor do par
   * PDF × PDF quando o estado gravado o traz (F-CP.9).
   */
  state: { page_a: number; page_b: number; pdf_pair_engine?: "channels" | "composite" };
  /** Os papéis trocados (o par B-contra-A do modo A + B). */
  inverted: boolean;
  /**
   * Qual dos dois motores compara este par — a pergunta é do PAR, não de cada
   * lado. `graphic` sobrepõe as pirâmides de separação; `document` compara
   * texto e vem com os dois lados abaixo; `mixed` é documento × arte — a
   * conferência de dizeres (03/11/2026, plano Pro), com `document_a` e
   * `packaging_b` no formato da sessão por slug. Ausente em backend anterior —
   * e aí é o gráfico de sempre.
   */
  compare_mode?: "graphic" | "document" | "mixed";
  /**
   * Os dois lados do par documental, no mesmo formato da view unitária: quem
   * os desenha é o mesmo `DocumentViewer`. `a` é o «antes» e `b` é o
   * «depois» — a ordem da emissão, que é a que a tela mostra. No par misto,
   * `document_a` é o documento (sempre à esquerda, qualquer que seja o lado
   * dele no link), no formato `ViewerV1MixedSide`.
   */
  document_a?: PublicViewByToken | ViewerV1MixedSide | null;
  document_b?: PublicViewByToken | null;
  /** Só no par misto: a arte, com o estado da leitura do Vision. */
  packaging_b?: ViewerV1MixedSide | null;
}

/**
 * De onde o diff documental é pedido.
 *
 * Um par de slugs na rota autenticada (`?diff=`), o token do link no par
 * público — que **não publica slug nenhum** (T-38), e foi por isso que o diff
 * precisou de porta própria. O payload que volta é o mesmo nos dois.
 */
export type DocumentCompareSource =
  | { kind: "slug"; left: string; right: string }
  /**
   * `inverted`: o par na outra ordem — «Trocar A e B» (24/10/2026). O link
   * guarda A e B na ordem da emissão; o servidor troca os dois lados.
   */
  | { kind: "public-pair"; token: string; inverted?: boolean }
  /**
   * Documento × arte de embalagem (fase DA/E4): `left` é o documento, `right`
   * é a arte, e o texto da arte é o que o Google Vision identificou. Não há
   * diff global — só a conferência por seleção, sempre do documento para a arte.
   */
  | {
      kind: "mixed-slug";
      left: string;
      right: string;
      /** Os nomes dos dois lados: o esboço de diff do par misto não os busca em lugar nenhum. */
      leftName?: string;
      rightName?: string;
    }
  /**
   * O mesmo par documento × arte no link público (03/11/2026): o token no
   * lugar dos dois slugs, que o link não publica (T-38). O servidor sabe qual
   * lado é o documento.
   */
  | { kind: "mixed-pair"; token: string; leftName?: string; rightName?: string };

/** O par documento × arte, por slug ou pelo token do link: conferência por seleção, sem diff global. */
export function isMixedDocumentSource(
  source: DocumentCompareSource | null | undefined,
): source is Extract<DocumentCompareSource, { kind: "mixed-slug" | "mixed-pair" }> {
  return source?.kind === "mixed-slug" || source?.kind === "mixed-pair";
}

export interface ViewerV1ImageResponse {
  blob: Blob;
  headers: Headers;
  elapsedMs: number;
}

/**
 * O que cada ferramenta faz de cada lado do par — publicado por E5.5.
 *
 * Três blocos porque são três perguntas: `left`/`right` é o que cada ARQUIVO
 * oferece, `both` é a interseção (útil só para decidir entre "por lado" e
 * "desabilitar de vez") e `compare_layer` é o que a CAMADA DE DIFERENÇA oferece,
 * que não é a mesma coisa — ela nunca teve pipeta em motor nenhum.
 */
export interface ViewerV1CompareTools {
  left?: { source_type?: string | null; capabilities?: Record<string, boolean> } | null;
  right?: { source_type?: string | null; capabilities?: Record<string, boolean> } | null;
  both?: Record<string, boolean>;
  compare_layer?: {
    channels?: boolean;
    eyedropper?: boolean;
    difference_regions?: boolean;
    region_export?: boolean;
    alignment?: boolean;
  } | null;
}

export interface ViewerV1Compare {
  compare_id: string;
  render_a_id: string;
  render_b_id: string;
  status: string;
  ready: boolean;
  difference_threshold: number;
  offset_x: number;
  offset_y: number;
  /** Raster physical alignment uses documented millimetres, never tile DPI. */
  alignment_coordinate_space?: "physical_mm_v1" | "pixel_v1" | string;
  offset_mm_x?: number;
  offset_mm_y?: number;
  reference_side?: "a" | "b";
  moving_side?: "a" | "b";
  channels: string[];
  preview_url: string;
  difference_regions_url?: string;
  tile_source?: ViewerV1TileSource | null;
  /**
   * "mixed" quando os dois lados não são da mesma natureza (E4). **Ausente no
   * payload do motor Rust** — para saber o tipo de cada lado nos dois motores,
   * use `tools.{left,right}.source_type`, que é a única fonte que os dois
   * publicam (ver `lib/compareStatus.ts`). `"pdf_composite"` (F-CP.9) é o par
   * PDF × PDF comparado pelo composto no motor raster — é por este campo que a
   * tela sabe o motor em vigor (`compareEngineKind`), nunca pelo perfil pedido.
   */
  source_type?: string | null;
  /**
   * O bloco de cada lado. O formato **difere por motor**: no raster é
   * `{render_id, page (1-based), source_type}` e no Rust é
   * `{artifact, generation_token, page (0-based)}`. Por isso a tela nunca lê a
   * página daqui — ela sabe qual está mostrando.
   */
  left?: Record<string, unknown> | null;
  right?: Record<string, unknown> | null;
  /**
   * F-CA.4 — o eco das combinações de camadas (forma canônica `on:…`), só
   * quando algum lado pediu; `a` é sempre do `render_a` do pedido. Os blocos
   * `left`/`right` trazem `layers` só no lado pedido.
   */
  layers?: { a: string | null; b: string | null } | null;
  /**
   * E3.2 — em que resolução a comparação da PÁGINA INTEIRA é feita, em px/mm.
   * `null` quando falta milímetro de um dos lados (o par cai em `pixel_v1` e
   * não há px/mm honesto a publicar); **ausente** no motor Rust, que não
   * declara nada disso.
   */
  analysis_px_per_mm?: number | null;
  /** Qual lado limita a resolução da análise: o que empresta 100 % da sua. */
  analysis_limited_by?: "a" | "b" | null;
  /** Quanto da resolução própria cada lado empresta — a régua do par sem mm. */
  analysis_native_fraction?: { a: number; b: number } | null;
  /** E4.5: a régua do perdão de registro, declarada em mm ou em pixel base. */
  registration_tolerance_mm?: number | null;
  registration_tolerance_px?: number | null;
  /**
   * E7.2 — **o que comparar**, no padrão deste par: `structure` (luminância
   * Rec. 709) no par misto, `appearance` (RGB por canal) no resto. É escolha de
   * VISTA: viaja no pedido, não na identidade do compare.
   */
  compare_mode?: string | null;
  /** As modalidades que este par aceita. */
  compare_modes?: string[] | null;
  tools?: ViewerV1CompareTools | null;
}

export interface ViewerV1DifferenceRegion {
  id: string;
  /** A caixa de AGRUPAMENTO, em pixels base da referência: é ela que enquadra o clique. */
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * Pixels de diferença de verdade — em pixels da GRADE DE ANÁLISE no motor
   * raster e em pixels BASE no motor Rust. As duas unidades diferem por ordens
   * de grandeza no mesmo trabalho físico, então **não filtre por este número**:
   * use `changed_area_mm2`, que é a mesma pergunta na régua do usuário.
   */
  changed_pixels: number;
  /** Área de diferença em mm². Ausente no par sem medida física (`pixel_v1`). */
  changed_area_mm2?: number | null;
  /** A caixa JUSTA da tinta: o tamanho da diferença, não o do agrupamento. */
  ink_box?: { x: number; y: number; width: number; height: number } | null;
  /** Fração da caixa de agrupamento que tem diferença, de 0 a 1. */
  fill_ratio?: number | null;
  /**
   * F-CP.5 — a classe da região: `text` quando metade ou mais da tinta dela
   * cai sobre o texto vivo de um dos PDFs; senão `art`. Ausente (backend
   * anterior) conta como arte.
   */
  region_class?: "text" | "art";
  /** F-CP.5 — que fração da tinta está sob o texto vivo, 0–1, duas casas. */
  text_coverage?: number;
  /**
   * F-CP.5b — sobre o que a cobertura foi medida: os pixels acesos da prova do
   * fino (`pixels`, motor raster) ou a caixa da tinta (`box`, Rust e lista antiga).
   */
  coverage_basis?: "pixels" | "box";
  /** F-CP.5b — de que lista a região veio: a do detector de texto ou a da arte. */
  detector?: "text" | "art";
}

/** F-CP.5b — a régua em que a lista de UM detector foi levantada. */
export interface ViewerV1DetectorRuler {
  threshold: number;
  solid_threshold: number;
  min_area_mm2: number | null;
  cache_status?: "hit" | "miss" | string | null;
  regions_lifted: number;
  regions_dropped_without_fine_ink?: number | null;
}

/** F-CP.5 — as duas classes de região da lista. */
export type ViewerV1RegionClass = "text" | "art";


export interface ViewerV1DifferenceRegions {
  schema_version: string;
  compare_id: string;
  source_type: "raster" | string;
  page: number;
  coordinate_space: {
    reference: "render_a" | string;
    unit: "px" | string;
    origin: "top_left" | string;
    width: number;
    height: number;
  };
  analysis_dimensions: { width: number; height: number };
  /**
   * Resolução da grade em que a diferença foi decidida. É o que traduz o
   * tamanho de um ponto da máscara para milímetros; só o motor raster publica.
   */
  analysis_px_per_mm?: number | null;
  difference_threshold: number;
  offset: { x: number; y: number };
  alignment_coordinate_space?: "physical_mm_v1" | "pixel_v1" | string;
  scale?: number;
  reference_side?: "a" | "b";
  regions: ViewerV1DifferenceRegion[];
  cache_status?: "hit" | "miss" | string;
  /** F-CP.8b — de que nível veio o perfil, só quando o pedido levou `public_view`. */
  profile_origin?: "link" | "plan" | "default" | string;
  /** F-CP.5 — quantas regiões de cada classe a lista traz (depois do filtro por classe). */
  region_classes?: { text: number; art: number };
  /** F-CP.5 — quantas o filtro por classe tirou (zero sem os quatro por classe). */
  regions_hidden_by_class?: { text: number; art: number };
  /** F-CP.5 — os lados cujo texto vivo classificou; vazio = sem texto vivo, tudo arte. */
  text_boxes_sides?: ("a" | "b")[];
  text_class_min_coverage?: number;
  /** F-CP.5 — o limiar por classe valeu? Falso no motor por tinta (sem intensidade). */
  class_thresholds_applied?: boolean;
  /** F-CP.5 — os quatro que valeram (o do par quando o pedido não mandou); área nula sem mm². */
  class_params_applied?: {
    threshold_text: number;
    threshold_art: number;
    min_area_text_mm2: number | null;
    min_area_art_mm2: number | null;
  };
  /** F-CP.5 — o limiar da camada pintada (o do par): a lista por classe pode mostrar mais ou menos. */
  painted_threshold?: number;
  live_text_ms?: number;
  /** F-CP.5 — o estado do texto vivo de cada lado (`ok`, `no_text`, `no_pdf`, `rotated`…). */
  live_text_status?: Partial<Record<"a" | "b", string>>;
  /** F-CP.5b — `pixels` só quando toda região da lista foi medida sobre os pixels acesos. */
  coverage_basis?: "pixels" | "box";
  /** F-CP.5b — quantas listas o motor levantou: 2 com limiar por classe diferente do par. */
  lists_computed?: number;
  /** F-CP.5b — a régua de cada detector; com uma lista só, a mesma nos dois. */
  thresholds_used?: Record<ViewerV1RegionClass, ViewerV1DetectorRuler>;
  /** F-CP.5b — as regiões de arte que eram a mesma marca de uma de texto (o texto vence). */
  regions_deduplicated?: number;
}

export interface ViewerV1AutoAlignResult {
  compare_id: string;
  offset_x: number;
  offset_y: number;
  offset_mm_x?: number;
  offset_mm_y?: number;
  alignment_coordinate_space?: "physical_mm_v1" | "pixel_v1" | string;
  physical_scale_x?: number;
  physical_scale_y?: number;
  scale: number;
  reference_side: "a" | "b";
  moving_side: "a" | "b";
  reference_dimensions: { width: number; height: number };
  confidence: number;
  matches: number;
  refined_alignment?: { refined: boolean; windows: number; residual_px?: number; scale_step?: number };
  /** O giro em que a medição fechou — pode diferir do pedido (os dois sentidos
      são medidos e vence o de maior consenso). */
  rotate_moving?: number;
  inliers: number;
  /** Fração da referência que os inliers cobrem: quanto da arte este encaixe
      realmente descreve. Confiança alta com cobertura baixa é o par com
      diagramações diferentes — o modelo fecha num painel e só nele. */
  coverage?: number;
  /** O backend já respondeu a pergunta: a cobertura ficou abaixo do limiar. */
  partial_model?: boolean;
  rotation_deg: number;
}

export interface ViewerV1ObjectAlignResult {
  compare_id: string;
  offset_x: number;
  offset_y: number;
  offset_mm_x?: number;
  offset_mm_y?: number;
  alignment_coordinate_space?: "physical_mm_v1" | "pixel_v1" | string;
  physical_scale_x?: number;
  physical_scale_y?: number;
  scale: number;
  reference_side: "a" | "b";
  moving_side: "a" | "b";
  reference_dimensions: { width: number; height: number };
  /** Score do template matching (0..1) — vale como confiança. */
  confidence: number;
  score: number;
  matched_box_b: { x: number; y: number; width: number; height: number };
  /** A escala que os milímetros dos dois lados previam, quando os dois a
      declaram — e se a busca ficou dentro dela. Fora do prior é legítimo
      (prova reduzida) e é o caso que merece conferência. */
  expected_scale?: number | null;
  scale_prior_used?: boolean;
  /**
   * E5.2 — **por qual regra** a escala foi prevista. As três respondem coisas
   * diferentes, e sem isto a tela não tem como explicar por que o encaixe ficou
   * daquele tamanho: `marked_objects` (a razão medida entre as duas marcas),
   * `pair_mm_per_px` (a razão mm/px do par, que só vale para a mesma peça) ou
   * `none` (não havia prior — o estimador varreu sozinho).
   */
  scale_prior_source?: "marked_objects" | "pair_mm_per_px" | "none" | string | null;
  /** Se o refino por features fechou consenso sobre o resultado do template. */
  refined?: boolean;
  rotation_deg: number;
}

export interface ViewerV1CompareSessionRender {
  render_id: string;
  status: string;
  ready: boolean;
  job_id: string | null;
  job_status: string | null;
  progress?: Record<string, unknown> | null;
  error?: string | null;
}

/**
 * Um lado do par documento × arte (fase DA/E2), no subconjunto do `by-slug`
 * que cada miolo lê: o `DocumentViewer` (documento) e o `ViewerCanvas` (arte).
 */
export interface ViewerV1MixedSide {
  viewer_kind: "document" | "packaging";
  /** Sem `public_slug` no link público (T-38). */
  file: { id: string; public_slug?: string; filename: string; asset_id: string | null };
  render: ViewerV1Render;
  tile_source: ViewerV1TileSource | null;
  view_info: ViewerV1ViewInfo | null;
  preprepare: Record<string, unknown> | null;
  /** Só no lado da arte: se o Vision já leu esta arte (I1). `missing` = nunca pedido. */
  text_analysis?: { status: string; analysis_id?: string | null; progress?: number | null } | null;
}

/**
 * O perfil de comparação que a sessão resolveu (Fase CP, plano §6.2). A tela
 * adota todos os valores como estado inicial, uma vez por compare; a vista
 * exportada vence. `threshold` já é o do par (PDF × PDF ou com imagem).
 */
export interface CompareProfilePayload {
  schema_version?: string;
  name: string;
  source: "default" | "test";
  threshold: number;
  threshold_pdf?: number;
  threshold_raster?: number;
  registration_tolerance_mm: number;
  registration_tolerance_px: number;
  aa_enabled: boolean;
  aa_factor: number;
  granularity: "fine" | "medium" | "coarse" | string;
  region_min_area_mm2: number;
  auto_align: boolean;
  /** F-CP.7 — o filtro de ruído do lado imagem; ausente (backend anterior) = `none`. */
  noise_filter?: "none" | "median3" | string;
  /**
   * F-CP.9 — o motor do par PDF × PDF: o estado inicial da caixa «Comparar por
   * separação de cores» (`channels` marcada, `composite` desmarcada). Ausente
   * (backend anterior) = `composite`.
   */
  pdf_pair_engine?: "channels" | "composite" | string;
  /**
   * F-CP.4 — as réguas das granulometrias, em mm, na ordem fina, média,
   * grossa. Ausente (backend anterior) = os padrões. A F-CP.10 as edita no
   * painel «Avançado».
   */
  granularity_rulers?: { dilation_mm?: number[]; fusion_mm?: number[]; cap_mm?: number[] };
  /**
   * F-CP.5 — limiar e área mínima por classe de região; `null` (ou ausente,
   * backend anterior) = o do par.
   */
  threshold_text?: number | null;
  threshold_art?: number | null;
  region_min_area_text_mm2?: number | null;
  region_min_area_art_mm2?: number | null;
}

/** Um item de `GET /viewer/v1/compare-profiles`: o perfil sem par, limiar nulo. */
export type CompareProfileListItem = Omit<CompareProfilePayload, "threshold"> & { threshold: number | null };

export interface ViewerV1CompareSession {
  schema_version: string;
  status: string;
  ready: boolean;
  /** `public_slug` não vem no link público de par (T-38): ali o token é o que abre. */
  file_a: { id: string; public_slug?: string; filename: string };
  file_b: { id: string; public_slug?: string; filename: string };
  render_a: ViewerV1CompareSessionRender;
  render_b: ViewerV1CompareSessionRender;
  compare: ViewerV1Compare | null;
  compare_id?: string | null;
  channels?: string[];
  tile_source?: ViewerV1TileSource | null;
  page_a: number;
  page_b: number;
  /** A capability do par (F-S6): cobre os dois lados. */
  url_capability?: UrlCapability | null;
  /**
   * Qual motor compara o par (fase DA/E2). Ausente em backend anterior, e
   * ausência é o gráfico de sempre. `mixed` = documento à esquerda, arte à
   * direita: não há `compare`; os lados vêm em `document_a` e `packaging_b`.
   */
  compare_mode?: "graphic" | "document" | "mixed";
  /** No par misto por slug é `ViewerV1MixedSide`; no par público documental, o lado da view. */
  document_a?: ViewerV1MixedSide | PublicViewByToken | null;
  packaging_b?: ViewerV1MixedSide | null;
  /**
   * A correspondência de tintas aplicada (rflow 2.3.5): proposta do servidor
   * ou a escolha mandada em `channel_map`. Só no par em que os dois lados são
   * PDF; ausente em imagem, par misto e backend anterior.
   */
  channel_correspondence?: ChannelCorrespondence | null;
  /**
   * O perfil resolvido (F-CP.2b). Só no par gráfico montado; ausente em
   * backend anterior, e ausência abre a tela com os valores de sempre.
   */
  profile?: CompareProfilePayload | null;
}

export interface PlateColor {
  slug: string;
  name: string;
  type: "process" | "spot" | "technical";
  rgb: string;
}

export interface AppSettingItem {
  key: string;
  /** Rótulo em português exibido na tela (vem do catálogo no backend). */
  label: string;
  description: string;
  /** Aba onde o item aparece. */
  tab: string;
  value: unknown;
  origin: "db" | "env" | "default";
  default: unknown;
  setting_class: "runtime" | "restart";
  type: string;
  requires_restart: boolean;
  unit: string | null;
  choices: string[] | null;
}

export type AssetSortOrder = "uploaded" | "name";

export interface AssetPage {
  items: AssetListItem[];
  /** null quando o total não é exato (filtro de status ativo). */
  total: number | null;
}

export interface AssetListItem {
  id: string;
  current_file_id?: string | null;
  public_slug?: string | null;
  path: string;
  folder_path: string;
  filename: string;
  source_type?: "pdf" | "raster" | string | null;
  /** Qual miolo do viewer este arquivo abre — decide o destino do «Comparar». */
  viewer_kind?: ViewerKind;
  /** O viewer escolhido à mão no gerenciador; `null`/ausente é automático. */
  viewer_kind_override?: ViewerKind | null;
  source_mime?: string | null;
  source_ext?: string | null;
  thumb_url: string | null;
  size_bytes: number | null;
  status: AssetStatus;
  created_at: string;
  last_accessed_at: string;
  render_seconds: number | null;
  render_dpi: number | null;
  render_version: number | null;
  render_mode: "channels" | "composite" | null;
  source_dpi?: number | null;
  has_channels?: boolean;
  channel_count?: number;
  notes_count: number;
  plates: PlateColor[];
  viewer_url: string | null;
  /**
   * Quantas separações a pasta deste conjunto tem.
   *
   * Presente só no card de conjunto: a listagem colapsa os N membros num card
   * só (F-T.8, §4.1), e este número é o selo que diz quantos ele representa.
   */
  separation_count?: number | null;
}

/** Uma versão do mesmo arquivo — o histórico que o banco sempre teve. */
export interface AssetVersionItem {
  id: string;
  sha256: string;
  created_at: string;
  file_size: number;
  page_count: number | null;
  source_type: string | null;
  /** O endereço estável desta versão: `File`↔`AssetVersion` é 1:1. */
  public_slug: string | null;
  filename: string | null;
  viewer_kind: ViewerKind;
  is_current: boolean;
  /** Sem render pronto não há semântica, e sem semântica não há comparação. */
  render_ready: boolean;
}

export interface FolderItem {
  name: string;
  path: string;
  asset_count: number;
  created_at?: string | null;
  /** Só vem quando a pasta é favorita — fica antes das outras, para todos. */
  favorite?: boolean;
}

export interface AssetEvent {
  id: string;
  event_type: string;
  occurred_at: string;
  metadata_json: Record<string, unknown> | null;
}

export interface RenderLogItem {
  id: string;
  file_id: string;
  render_id: string | null;
  job_id: string | null;
  celery_task_id: string | null;
  task_type: string;
  level: "debug" | "info" | "warning" | "error" | "critical";
  event: string;
  message: string;
  metadata_json: Record<string, unknown> | null;
  occurred_at: string;
}

export interface FolderListResponse {
  current: string;
  subfolders: FolderItem[];
}

export interface FolderFavoriteResult {
  path: string;
  favorite: boolean;
}

export interface FolderOperationResult {
  path: string;
  affected_assets: number;
  affected_files: number;
  deleted_objects: number;
}

export interface AssetOperationResult {
  asset_id: string;
  path: string | null;
  affected_files: number;
  deleted_objects: number;
}

export interface NotePreview {
  id: string;
  page: number;
  x_mm: number;
  y_mm: number;
  body: string;
  author: string;
  created_at: string;
}

/** Faixas de uma âncora de texto, em `document_page_top_left_v1` (pt). */
export type NoteTextAnchorRect = [number, number, number, number];

export interface NoteTextAnchor {
  /** Offsets no `logical_text` da página. */
  start: number;
  end: number;
  rects: NoteTextAnchorRect[];
}

export type NoteKind = "note" | "highlight";

export interface Note {
  id: string;
  asset_id: string;
  page: number;
  x_mm: number;
  y_mm: number;
  /** Nota de seleção: (x_mm, y_mm) é o canto superior-esquerdo do retângulo. Nulos = nota de ponto. */
  w_mm: number | null;
  h_mm: number | null;
  /** 'highlight' é marca-texto: mesma tabela, corpo vazio, âncora de texto. */
  kind: NoteKind;
  /** Presente só nas notas presas ao texto do viewer de documento. */
  text_anchor: NoteTextAnchor | null;
  /**
   * Em que leitura da peça de separações a nota foi feita (F-T.14). A lista é
   * uma só para LEN e VIEW; isto é a etiqueta. Nula fora de peça com VIEW e em
   * backend anterior.
   */
  variant?: SeparationVariant | null;
  body: string;
  author: string;
  author_email: string | null;
  is_completed: boolean;
  /**
   * Quem finalizou a nota, e quando. O servidor carimba na transição para
   * concluída, a partir de quem chamou a rota — não vai no PATCH. Nulos numa
   * nota pendente e em toda nota concluída antes da migração 0057, que fica
   * sem carimbo: `updated_at` é de qualquer edição, não da conclusão.
   */
  completed_at: string | null;
  completed_by: string | null;
  completed_by_email: string | null;
  parent_note_id: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NoteVersion {
  id: string;
  note_id: string;
  version: number;
  body: string;
  author: string;
  author_email: string | null;
  is_completed: boolean;
  created_at: string;
}

export interface NoteCreateInput {
  page: number;
  x_mm: number;
  y_mm: number;
  w_mm?: number | null;
  h_mm?: number | null;
  body: string;
  author: string;
  author_email?: string | null;
  parent_note_id?: string | null;
  kind?: NoteKind;
  text_anchor?: NoteTextAnchor | null;
  /** Só numa peça com VIEW (F-T.14); fora dela não vai. */
  variant?: SeparationVariant | null;
}

export interface NoteUpdateInput {
  page?: number;
  x_mm?: number;
  y_mm?: number;
  body?: string;
  author?: string;
  author_email?: string | null;
  is_completed?: boolean;
  parent_note_id?: string | null;
  updated_at?: string;
}

export interface NotesPreviewResponse {
  page: number;
  thumb_url: string | null;
  thumb_width_px: number | null;
  thumb_height_px: number | null;
  thumb_width_mm: number | null;
  thumb_height_mm: number | null;
  cache_metrics: ViewerCacheMetrics | null;
  notes: NotePreview[];
  /**
   * `render.timings` do render atual — o tempo de trabalho deste viewer, sem
   * fila. O painel do arquivo no manager é quem mostra o tempo de render desde
   * que o bloco saiu do painel técnico do viewer (03/set).
   */
  render_timings?: Record<string, unknown> | null;
  /** O tempo do ProofScope para o mesmo arquivo; `null` sem medida ou sem ponte. */
  proofscope?: ViewerV1ProofscopeRender | null;
}

export interface ViewerCacheMetrics {
  mask_bytes_total: number | null;
  mask_object_count: number;
  mask_page_count: number;
  mask_source: "indexed" | "local" | "missing" | "unknown";
}

export interface ColorPickerChannel {
  slug: string;
  name: string;
  coverage_pct: number;
  color: string;
  /** Faca, cota, substrato: lidos na pipeta, fora do TAC. */
  is_technical?: boolean;
}

export interface ColorPickerResult {
  x_px: number;
  y_px: number;
  channels: ColorPickerChannel[];
  tac: number;
  tac_alert: boolean;
}

// ---------- Medição de retícula (F-T.6) ----------

/** A janela efetivamente lida — depois de centrar no clique e grudar na borda. */
export interface ScreenMeasureWindow {
  x: number;
  y: number;
  size: number;
  requested_x: number;
  requested_y: number;
}

/**
 * O que o endpoint devolve: a medida, a janela e as retículas **declaradas**.
 *
 * O declarado vem junto de propósito — a tela mostra "medi isto, e o arquivo
 * declara aquilo" numa chamada só.
 */
/** A medição de **uma** tinta na janela — uma linha da tabela (F-T.10, §5.2). */
export interface ScreenMeasureInk {
  channel: string;
  slug?: string | null;
  /** `confirmed` | `uncertain` | `rejected` | `unsupported_fm`. */
  verdict: string;
  /**
   * Código fechado do porquê; `lib/screening.ts` traduz.
   *
   * `no_ink_in_window` é deliberadamente distinto de `flat_coverage` (sólido) e
   * de `weak_orthogonality` (tentei e recusei): os três dizem coisas diferentes.
   */
  reason?: string | null;
  frequency_lpi?: number | null;
  angle_deg?: number | null;
  angle_direction?: string | null;
  orthogonality?: number | null;
  coverage_pct: number;
  matched_screen?: ScreenSetPayload | null;
  match_delta_lpi_pct?: number | null;
  match_delta_angle_deg?: number | null;
  declared_screens?: ScreenSetPayload[] | null;
  /** O declarado que casaria se o arquivo estivesse gravado espelhado (§19.2). */
  mirrored_screen?: ScreenSetPayload | null;
  /** `egGr:scanlinedirection` verbatim, ao lado do aviso. Não é regra. */
  scanline_direction?: string | null;
}

/** Uma janela, todas as tintas — o que a ferramenta de área devolve. */
export interface ScreenMeasureResult {
  /** A resolução real do nível 0: 4000 dpi no job XPS, 2400 no NX. */
  dpi: number;
  /** A janela efetivamente lida. É ela que a tela desenha, não a seleção. */
  window: ScreenMeasureWindow;
  /** A seleção que foi mandada, `[x, y, w, h]`. */
  selection?: number[] | null;
  inks: ScreenMeasureInk[];
}

/** Uma retícula declarada no XMP, como o contrato da T.3 publica. */
export interface ScreenSetPayload {
  frequency_lpi?: number | null;
  angle_deg?: number | null;
  angle_direction?: string | null;
  dot_shape?: string | null;
  dot_name?: string | null;
  frequency_requested_lpi?: number | null;
  angle_requested_deg?: number | null;
  /** A curva DGC que esta retícula aplica, **pelo nome** (F-T.10, §4.1). */
  dgc_lw?: string | null;
  dgc_ct?: string | null;
}

// ---------- Queue / Jobs (F1.18) ----------

export type QueueJobStatus =
  | "queued"
  | "dispatched"
  | "preempting"
  | "preempted"
  | "started"
  | "progress"
  | "success"
  | "failure"
  | "retry"
  | "canceled";

export type QueueJobType =
  | "viewer.render"
  | "viewer.preprepare"
  | "viewer.rehydrate"
  | "viewer.layers"
  | "render.preview"
  | "barcode";

export interface QueueJob {
  id: string;
  type: QueueJobType;
  status: QueueJobStatus;
  priority: number;
  progress: number;
  progress_message: string | null;
  file_id: string | null;
  file_name: string | null;
  file_path: string | null;
  error: string | null;
  attempts: number;
  celery_task_id: string | null;
  render_id: string | null;
  queued_at: string;
  dispatched_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  preempt_requested_at: string | null;
  preempted_at: string | null;
  preempted_by_job_id: string | null;
  preempt_count: number;
  heartbeat_at: string | null;
  operational_note: string | null;
}

export interface QueueJobDiagnostics {
  job_id: string;
  celery_task_id: string | null;
  celery_state: "active" | "reserved" | "scheduled" | "not_found" | "missing_task_id" | string;
  task_found: boolean;
  worker_hostname: string | null;
  worker_pid: number | null;
  task_name: string | null;
  queue: string | null;
  acknowledged: boolean | null;
  active_seconds: number | null;
  heartbeat_age_seconds: number | null;
  checked_at: string;
  diagnosis: string;
}

export interface QueueJobListResponse {
  items: QueueJob[];
  total: number;
}

export interface QueueSummary {
  by_status: Partial<Record<QueueJobStatus, number>>;
  started_last_hour: number;
  success_rate_24h: number | null;
  avg_duration_24h_seconds: number | null;
}

export interface QueueListParams {
  status?: QueueJobStatus[];
  type?: QueueJobType;
  file_id?: string;
  from?: string;
  to?: string;
  q?: string;
  priority?: number;
  offset?: number;
  limit?: number;
}

// ---------- Organizações do LinkFlow (21/10/2026) ----------

/** Uma empresa do cadastro: o id é a chave, o nome é o último que chegou. */
export interface OrganizationSummary {
  id: string;
  name: string | null;
  first_seen_at: string;
  last_seen_at: string;
  /** Links emitidos para ela nos últimos 30 dias. */
  links_30d: number;
  /** O plano do último link (o efetivo de hoje pode ser outro, com o teste do Pro). */
  last_plan: PlanName | null;
  dictionary_accepted: number;
  dictionary_blocked: number;
}

// ---------- Uso dos recursos (21/10/2026) ----------

export type UsageSurface = "gerenciador" | "link_viewer" | "link_compare" | "link_exportado";
export type UsageScope = "viewer" | "compare";
export type UsagePlan = PlanName | "sessao" | "";

export interface UsageCounts {
  /** Aberturas que usaram o recurso (na linha das aberturas, as aberturas). */
  openings: number;
  clicks: number;
  locked_clicks: number;
}

export interface UsagePanelFeature {
  feature: string;
  /** Por escopo, uma posição por dia de `days`. */
  days: Record<UsageScope, { openings: number[]; clicks: number[]; locked_clicks: number[] }>;
  by_cut: Array<UsageCounts & { surface: UsageSurface; plan: UsagePlan; scope: UsageScope }>;
  by_org: Array<UsageCounts & { organization_id: string; plan: UsagePlan; scope: UsageScope }>;
}

export interface UsagePanel {
  days: string[];
  openings: {
    by_day: Record<UsageScope, number[]>;
    by_cut: Array<{ surface: UsageSurface; plan: UsagePlan; scope: UsageScope; openings: number }>;
    by_org: Array<{ organization_id: string; plan: UsagePlan; scope: UsageScope; openings: number }>;
  };
  features: UsagePanelFeature[];
  catalog: Array<{
    feature: string;
    scopes: UsageScope[];
    tool_key: string | null;
    min_plan: PlanName | null;
    session_only: boolean;
  }>;
  organizations: Array<{ id: string; name: string | null }>;
}

export interface UsagePanelParams {
  days: number;
  surface?: UsageSurface;
  plan?: PlanName | "sessao";
  /** Uma empresa; `""` são as aberturas sem empresa. */
  organization_id?: string;
  viewer_kind?: "packaging" | "document";
  /** O uso interno (gerenciador, link exportado, e-mail @linkflow.com.br) só entra quando pedido. */
  include_internal?: boolean;
}

/** Para onde vai o pacote: o token do link do LinkFlow, o do link exportado, ou a sessão. */
export type UsageTarget =
  | { kind: "public"; token: string }
  | { kind: "shared"; token: string; openingId: string; viewerKind: "packaging" | "document" }
  | { kind: "session"; openingId: string; scope: UsageScope; viewerKind: "packaging" | "document" };

export interface UsageEvent {
  feature: string;
  clicks: number;
  locked_clicks: number;
}

// ---------- Auditoria de acessos (set/2026) ----------

/** `viewer`: abriu uma arte pelo LinkFlow; `compare`: comparou duas. */
export type AuditLogKind = "viewer" | "compare";

export type AuditLogViewerKind = "document" | "packaging";

/** Uma abertura ou comparação feita pelo LinkFlow — o retrato do link emitido. */
export interface AuditLogEntry {
  id: string;
  created_at: string;
  kind: AuditLogKind;
  requester_name: string;
  requester_email: string | null;
  file_name: string;
  file_path: string;
  file_b_name: string | null;
  file_b_path: string | null;
  viewer_kind: AuditLogViewerKind;
  /**
   * O slug da versão que o link abriu (out/2026) — por ele a tela reabre o que a
   * pessoa viu. Nulo quando o link já saiu do banco; ausente em backend antigo.
   */
  file_slug?: string | null;
  file_b_slug?: string | null;
  /** A empresa do link (21/10/2026): o id e o nome do dia. Nulos sem empresa. */
  organization_id?: string | null;
  organization_name?: string | null;
}

export interface AuditLogListResponse {
  items: AuditLogEntry[];
  total: number;
}

export interface AuditLogParams {
  from?: string;
  to?: string;
  q?: string;
  kind?: AuditLogKind;
  viewer_kind?: AuditLogViewerKind;
  /** Uma empresa; `""` são os links que chegaram sem empresa. */
  organization_id?: string;
  offset?: number;
  limit?: number;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  /** Senha definida por outra pessoa: precisa ser trocada antes de usar o sistema. */
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: AuthUser;
}

/** Admin lista de usuários — mesmo formato que sessão/me. */
export type UserOut = AuthUser;

export interface UserCreateIn {
  name: string;
  email: string;
  password: string;
  is_active?: boolean;
  is_admin?: boolean;
}

export interface UserUpdateIn {
  name?: string;
  is_active?: boolean;
  is_admin?: boolean;
}

export interface ResetPasswordIn {
  password: string;
}

export interface ApiKeyOut {
  id: string;
  user_id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface ApiKeyCreateIn {
  name: string;
  expires_at?: string | null;
}

export interface ApiKeyCreateOut {
  api_key: string;
  key: ApiKeyOut;
}

// ---------- Insights (F1.20) ----------

export interface InsightOverview {
  avg_render_seconds: number | null;
  renders_per_hour: number;
  renders_last_hour: number;
  renders_count: number;
  renders_success_count: number;
  renders_failure_count: number;
  renders_under_10s_count: number;
  renders_10_30s_count: number;
  renders_30_60s_count: number;
  renders_over_60s_count: number;
  queue_render_under_10s_count: number;
  queue_render_10_30s_count: number;
  queue_render_30_60s_count: number;
  queue_render_over_60s_count: number;
  success_rate: number | null;
  /**
   * As categorias de armazenamento (Fase 10), um lugar da arquitetura cada,
   * disjuntas e prontas: a tela não deriva nenhuma delas. `other` é a série
   * fina do gráfico — sem card, e crescer é sintoma.
   *
   * `separations` é a sexta, da F-T.7: a pirâmide do conjunto de separações do
   * Esko, só no disco. Ela cresce ~222 MB por job e não cabe em nenhuma das
   * outras — refazê-la é uma renderização inteira, não uma composição.
   */
  originals_bytes_total: number;
  masks_local_bytes_total: number;
  ready_images_bytes_total: number;
  separations_bytes_total: number;
  documents_local_bytes_total: number;
  cold_bytes_total: number;
  other_bytes_total: number;
  pdf_base_bytes_total: number;
  renders_base_bytes_total: number;
  database_bytes_total: number;
  minio_bytes_total: number;
  viewer_cache_minio_bytes_total: number;
  viewer_cache_indexed_bytes_total: number;
  viewer_cache_mask_bytes_total: number;
  viewer_local_cache_bytes_total: number;
  viewer_local_image_cache_bytes_total: number;
  /**
   * F-GC.6 — o disco contra o alvo e o que o GC por disco está fazendo, do
   * snapshot mais recente. O percentual sai pronto do servidor com a régua do
   * GC e do `df` (usado ÷ (usado + livre)); a tela só formata. `null` é «não
   * medido» (snapshot anterior à F-GC.6, GC sem passada publicada). Os
   * contadores são das 24 h anteriores à coleta — o período escolhido não os
   * muda.
   */
  disk_total_bytes: number | null;
  disk_used_bytes: number | null;
  disk_free_bytes: number | null;
  disk_used_percent: number | null;
  disk_target_percent: number | null;
  /** «Retendo arquivos usados desde»: o menor último uso entre quem tem bytes. */
  retained_since: string | null;
  oldest_pyramid_ready_at: string | null;
  /** Quando terminou a passada do GC de onde vieram as duas datas acima. */
  gc_pass_at: string | null;
  expired_assets_24h: number;
  expired_bytes_24h: number;
  exists_false_24h: number;
  cold_opens_24h: number;
  watch_bytes_total: number;
  thumbs_bytes_total: number;
  pyramid_base_bytes_total: number;
  pyramid_levels_bytes_total: number;
  /** "Máscaras no disco" menos a pirâmide, calculado no servidor. */
  masks_other_bytes_total: number;
  snapshot_count: number;
}

export interface InsightsTimeseriesPoint {
  bucket_start: string;
  value: number;
  is_imputed: boolean;
}

export interface InsightsTimeseries {
  metric: string;
  range: string;
  points: InsightsTimeseriesPoint[];
}

/** Unidades do período por quem pediu a análise; `null` é o passado reconstruído. */
export interface OcrUsageOrigin {
  origin: string | null;
  units: number;
  pages: number;
}

/**
 * A seção «Custo com OCR» (06/10/2026), pronta: unidades do livro-razão do
 * Google Vision no período, no mês (calendário UTC) e no anterior, a estimativa
 * em dólares pela tabela configurada e as duas chaves em vigor. A tela formata
 * (`lib/ocrCost`) e nada mais.
 */
export interface OcrUsageSummary {
  range: string;
  units_in_range: number;
  failed_units_in_range: number;
  pages_in_range: number;
  analyses_in_range: number;
  units_month: number;
  units_previous_month: number;
  month_start: string;
  free_units_per_month: number;
  billable_units_month: number;
  estimated_cost_month_usd: number;
  estimated_cost_previous_month_usd: number;
  /** Unidades do período a preço cheio, sem franquia. */
  estimated_cost_in_range_list_usd: number;
  price_usd_per_1000: number;
  by_origin: OcrUsageOrigin[];
  points: InsightsTimeseriesPoint[];
  auto_enabled: boolean;
  vision_enabled: boolean;
  last_event_at: string | null;
}

export interface TranslationUsageTotal {
  calls: number;
  chars: number;
  cost_usd: number;
}

/** A seção «Tradução do Ler Texto» de Insights (TR1): o livro-razão das chamadas ao motor. */
export interface TranslationUsageSummary {
  range: string;
  range_hours: number;
  period: TranslationUsageTotal;
  month: TranslationUsageTotal;
  previous_month: TranslationUsageTotal;
  by_engine: (TranslationUsageTotal & { engine: string })[];
}

export interface InsightsSnapshotRun {
  task_id: string | null;
  message: string;
  snapshot_id: string | null;
  bucket_start: string | null;
  renders_count: number | null;
  partial: boolean | null;
  force: boolean;
}

export type InsightsRange = "24h" | "7d" | "30d";

/**
 * As séries que a tela busca a cada refresh — uma requisição por métrica: as
 * duas de operação e as da pilha de armazenamento (`STORAGE_STACK`), que é a
 * fonte única delas.
 *
 * As métricas antigas de storage (`pdf_base_*`, `renders_base_*`, `minio_*`,
 * `viewer_cache_*`, `viewer_local_*`) continuam válidas na API, mas saíram
 * daqui junto com os painéis que as mostravam: nenhuma delas descrevia um
 * lugar da arquitetura, e três se sobrepunham entre si.
 */
export const INSIGHTS_METRICS: readonly string[] = [
  "avg_render_seconds",
  "renders_count",
  ...STORAGE_STACK_METRICS,
];

export type InsightsMetric = (typeof INSIGHTS_METRICS)[number];

/**
 * O erro tipado das respostas não-ok vive em `apiError.ts` e é reexportado aqui:
 * `import { ApiError } from "@/lib/api"` continua sendo o caminho de sempre.
 */
import { apiErrorFromResponse } from "./apiError";

export { ApiError, apiErrorFromResponse } from "./apiError";

/**
 * Entrega ao browser o corpo de uma resposta como arquivo salvo.
 *
 * O nome vem do `Content-Disposition`: é o servidor que sabe o nome original
 * do asset, e `fallbackName` só cobre a resposta que não trouxer o header —
 * o que acontece de verdade quando a chamada é cross-origin e o cabeçalho não
 * está exposto. Por isso o `fallbackName` de cada chamada tem de ser o nome
 * que a tela já conhece, e não uma constante: quem passa `"arquivo.pdf"` fixo
 * faz um JPEG ser baixado como PDF. A leitura mora em `downloadName.ts`.
 */
async function saveResponseAsDownload(res: Response, fallbackName: string): Promise<void> {
  if (!res.ok) {
    // A mensagem sai de `mensagemDaFalha`, e o corpo cru vai para o console: o
    // que a tela mostrava antes era o erro do `JSON.parse` («Unexpected token
    // \'<\'» diante de uma página de erro do nginx), que esconde o status — a
    // única coisa que diz de quem é o problema.
    const corpo = await res.text().catch(() => "");
    if (corpo && !corpo.trim().startsWith("{")) {
      console.error("download falhou", res.status, res.statusText, corpo.slice(0, 2000));
    }
    throw new Error(mensagemDaFalha(res.status, res.statusText, corpo));
  }

  const objectUrl = URL.createObjectURL(await res.blob());
  const filename = nomeDoDownload(res.headers.get("content-disposition"), fallbackName);

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Revogar depois de um tick para o browser ter tempo de iniciar o download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }
  return (await res.json()) as T;
}

let _refreshPromise: Promise<string> | null = null;

async function _doRefresh(): Promise<string> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const rt = getRefreshToken();
      if (!rt) throw new Error("no refresh token");
      const res = await apiFetch(
        `${API_BASE}/auth/refresh`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
        },
        { public: true, _retried: true },
      );
      if (!res.ok) throw new Error(`refresh failed: ${res.status}`);
      const session = (await res.json()) as AuthSession;
      setAccessToken(session.access_token);
      setRefreshToken(session.refresh_token);
      return session.access_token;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

interface ApiFetchOptions {
  public?: boolean;
  _retried?: boolean;
  /** Teto de tempo da requisição. `0` desliga. */
  timeoutMs?: number;
}

/** Teto padrão: nenhuma chamada da API deve ficar pendurada para sempre. */
const DEFAULT_TIMEOUT_MS = 60_000;
/** Listagens de poll: se passar disso, a resposta já não serve. */
export const POLL_TIMEOUT_MS = 20_000;

/**
 * Junta o `signal` de quem chamou com um timeout próprio.
 *
 * Feito à mão em vez de `AbortSignal.any`, que ainda não existe em todos os
 * navegadores que abrem o viewer. `dispose()` tem de ser chamado sempre, senão
 * o timer segura o callback e o listener segura o signal de fora.
 */
function withTimeout(
  signal: AbortSignal | undefined | null,
  timeoutMs: number,
): { signal: AbortSignal | undefined; dispose: () => void } {
  if (!timeoutMs) return { signal: signal ?? undefined, dispose: () => {} };
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new DOMException("Tempo esgotado.", "TimeoutError")),
    timeoutMs,
  );
  const onAbort = () => controller.abort(signal?.reason);
  if (signal) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", onAbort, { once: true });
  }
  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    },
  };
}

async function apiFetch(
  input: string,
  init?: RequestInit,
  options?: ApiFetchOptions,
): Promise<Response> {
  const headers = new Headers(init?.headers ?? {});
  // Guardado para o ramo de 401: «havia token?» decide se há sessão a renovar.
  const token = options?.public ? null : getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // A capability da URL (F-S6): toda chamada do viewer (`/api/viewer/v1/…` e
  // `/vc/…`) leva o `k` do bootstrap em vigor, substituindo o que o backend
  // tiver carimbado — o carimbado pode ser de antes da renovação. Fora
  // dessas rotas `comCapability` devolve a URL como veio.
  const normalizedInput = comCapability(toSameOriginPath(input) ?? input);
  // Sem repetição em falha de rede: o bloco que existia aqui refazia o fetch
  // com `toSameOriginPath(input)` quando ele diferisse de `normalizedInput` —
  // e como `normalizedInput` é justamente `toSameOriginPath(input) ?? input`,
  // a condição nunca era verdadeira. Era código inalcançável, não retry.
  const { signal, dispose } = withTimeout(
    init?.signal,
    options?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  let res: Response;
  try {
    res = await fetch(normalizedInput, { ...init, headers, signal });
  } finally {
    dispose();
  }

  if (res.status === 401 && !options?.public && !options?._retried) {
    // Só limpa quando havia token para limpar (T-57): o anônimo de `/v/{slug}`
    // não tem sessão a renovar nem a encerrar — e apagar o storage aqui
    // arrastava junto quem estava logado em outra aba.
    const saida = await recoverFrom401({
      accessToken: token,
      refreshToken: getRefreshToken(),
      refresh: _doRefresh,
      clearTokens: clearSessionTokens,
      announceExpired: announceSessionExpired,
    });
    if (saida.kind !== "retry") throw new Error(mensagemDo401(saida.kind));
    const retryHeaders = new Headers(init?.headers ?? {});
    retryHeaders.set("Authorization", `Bearer ${saida.accessToken}`);
    return apiFetch(
      input,
      { ...init, headers: retryHeaders },
      { ...(options ?? {}), _retried: true },
    );
  }

  return res;
}

/**
 * Avisa a tela que a sessão acabou. Evento em vez de import para não fechar um
 * ciclo entre `api.ts` e o `authStore`; quem escuta hoje é o `ProtectedRoute`.
 */
function announceSessionExpired(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("linkflow:session-expired"));
  }
}

export interface UploadProgress {
  loaded: number;
  total: number | null;
  percent: number | null;
}

class XhrHttpError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function errorMessageFromBody(status: number, statusText: string, text: string): string {
  try {
    const body = JSON.parse(text) as Record<string, unknown>;
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // Keep the raw response below when the body is not JSON.
  }
  return `${status} ${statusText}: ${text}`;
}

function uploadFormDataWithProgress<T>(
  input: string,
  body: FormData,
  onProgress: ((progress: UploadProgress) => void) | undefined,
  token: string | null,
): Promise<T> {
  const normalizedInput = toSameOriginPath(input) ?? input;

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", normalizedInput);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return;
      const total = event.lengthComputable ? event.total : null;
      onProgress({
        loaded: event.loaded,
        total,
        percent: total ? Math.round((event.loaded / total) * 100) : null,
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch (err) {
          reject(err);
        }
        return;
      }
      reject(
        new XhrHttpError(
          xhr.status,
          errorMessageFromBody(xhr.status, xhr.statusText, xhr.responseText),
        ),
      );
    };
    xhr.onerror = () => reject(new Error("Falha de rede durante o upload."));
    xhr.send(body);
  });
}

/**
 * O link exportado do compare (`?view=`) como credencial das ferramentas de
 * leitura, para quem o abre **sem sessão** (pedido do dono, 21/10/2026): Ler
 * Código, Ler Braile, Ler Texto, conta-gotas e Medir Texto vão por
 * `/viewer/v1/shared-views/{token}/tools/*`, que confere o alcance do link.
 *
 * Estado de módulo, como a capability da URL (`lib/capability.ts`): as
 * ferramentas montam dentro do canvas e chamam a API de dentro dos stores, e
 * o token teria de descer por cinco componentes e dois stores. Quem liga é o
 * `Compare`, só sem sessão e com o link válido; ao sair da tela, ele desliga.
 * O token da view pública, quando vem, continua vencendo.
 */
let sharedViewToolsToken: string | null = null;

export function setSharedViewToolsToken(token: string | null): void {
  sharedViewToolsToken = token || null;
}

/** Onde mora cada ferramenta: o link público, o link exportado ou a sessão. */
function toolsRoute(publicViewToken?: string | null): { base: string; options?: ApiFetchOptions } {
  if (publicViewToken) return { base: `${API_BASE}/public-views/${publicViewToken}/tools`, options: { public: true } };
  if (sharedViewToolsToken) {
    return {
      base: `${API_BASE}/viewer/v1/shared-views/${encodeURIComponent(sharedViewToolsToken)}/tools`,
      options: { public: true },
    };
  }
  return { base: `${API_BASE}/tools` };
}

/**
 * A identificação de fonte (F-MT.4) só existe com sessão: nem o link público
 * de viewer nem o link exportado do compare têm a rota.
 */
export function fontIdentificationAvailable(publicViewToken?: string | null): boolean {
  return !publicViewToken && !sharedViewToolsToken;
}

export const api = {
  readObject(body: ObjectReadRequest, signal?: AbortSignal): Promise<ObjectReadResult> {
    return apiFetch(`${API_BASE}/tools/object-read`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal,
    }).then(r => jsonOrThrow<ObjectReadResult>(r));
  },

  base: API_BASE,

  login(email: string, password: string): Promise<AuthSession> {
    return apiFetch(
      `${API_BASE}/auth/login`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      },
      { public: true },
    ).then((r) => jsonOrThrow<AuthSession>(r));
  },

  logout(): Promise<void> {
    return apiFetch(`${API_BASE}/auth/logout`, { method: "POST" }).then((r) => {
      if (!r.ok) throw new Error(`logout failed: ${r.status}`);
    });
  },

  me(): Promise<{ user: AuthUser }> {
    return apiFetch(`${API_BASE}/auth/me`).then((r) => jsonOrThrow<{ user: AuthUser }>(r));
  },

  /**
   * Troca a senha e recebe **tokens novos**.
   *
   * A resposta passou de `{user}` para a sessão inteira na F-S2: trocar a senha
   * agora invalida as credenciais antigas (`token_version`), e sem o par novo
   * quem troca a própria senha se deslogaria na requisição seguinte. No
   * primeiro acesso obrigatório isso seria um laço fechado.
   */
  changePassword(currentPassword: string, newPassword: string): Promise<AuthSession> {
    return apiFetch(`${API_BASE}/auth/change-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }).then((r) => jsonOrThrow<AuthSession>(r));
  },

  refresh(refreshToken: string): Promise<AuthSession> {
    return apiFetch(
      `${API_BASE}/auth/refresh`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
      { public: true },
    ).then((r) => jsonOrThrow<AuthSession>(r));
  },


  listFolders(
    prefix: string,
    opts?: { search?: string; recursive?: boolean; signal?: AbortSignal },
  ): Promise<FolderListResponse> {
    const params = new URLSearchParams({ prefix });
    if (opts?.search) params.set("search", opts.search);
    if (opts?.recursive !== undefined) params.set("recursive", String(opts.recursive));
    return apiFetch(
      `${API_BASE}/folders?${params}`,
      { signal: opts?.signal },
      { timeoutMs: POLL_TIMEOUT_MS },
    ).then((r) => jsonOrThrow<FolderListResponse>(r));
  },

  createFolder(path: string): Promise<FolderOperationResult> {
    return apiFetch(`${API_BASE}/folders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
    }).then((r) => jsonOrThrow<FolderOperationResult>(r));
  },

  setFolderFavorite(path: string, favorite: boolean): Promise<FolderFavoriteResult> {
    return apiFetch(`${API_BASE}/folders/favorite`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, favorite }),
    }).then((r) => jsonOrThrow<FolderFavoriteResult>(r));
  },

  renameFolder(oldPath: string, newPath: string): Promise<FolderOperationResult> {
    return apiFetch(`${API_BASE}/folders/rename`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ old_path: oldPath, new_path: newPath }),
    }).then((r) => jsonOrThrow<FolderOperationResult>(r));
  },

  deleteFolder(path: string): Promise<FolderOperationResult> {
    const params = new URLSearchParams({ path });
    return apiFetch(`${API_BASE}/folders?${params}`, { method: "DELETE" }).then((r) =>
      jsonOrThrow<FolderOperationResult>(r),
    );
  },

  renameAsset(assetId: string, newFilename: string): Promise<AssetOperationResult> {
    return apiFetch(`${API_BASE}/assets/${assetId}/rename`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ new_filename: newFilename }),
    }).then((r) => jsonOrThrow<AssetOperationResult>(r));
  },

  deleteAsset(assetId: string): Promise<AssetOperationResult> {
    return apiFetch(`${API_BASE}/assets/${assetId}`, { method: "DELETE" }).then((r) =>
      jsonOrThrow<AssetOperationResult>(r),
    );
  },

  // --- Configuração (Fase 5 C2/C3) ---

  listAppSettings(): Promise<AppSettingItem[]> {
    return apiFetch(`${API_BASE}/admin/settings`).then((r) => jsonOrThrow<AppSettingItem[]>(r));
  },

  updateAppSetting(key: string, value: unknown): Promise<AppSettingItem> {
    return apiFetch(`${API_BASE}/admin/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    }).then((r) => jsonOrThrow<AppSettingItem>(r));
  },

  resetAppSetting(key: string): Promise<AppSettingItem> {
    return apiFetch(`${API_BASE}/admin/settings/${encodeURIComponent(key)}`, {
      method: "DELETE",
    }).then((r) => jsonOrThrow<AppSettingItem>(r));
  },

  listAssets(opts: {
    folder?: string;
    search?: string;
    status?: AssetStatus;
    has_notes?: boolean;
    /** `document` ou `packaging`: qual miolo do viewer o arquivo abre. */
    viewer_kind?: ViewerKind;
    recursive?: boolean;
    sort?: AssetSortOrder;
    limit?: number;
    offset?: number;
  }): Promise<AssetListItem[]> {
    return api.listAssetsPage(opts).then((page) => page.items);
  },

  /** Uma página de assets + total (quando o backend consegue calcular). */
  async listAssetsPage(opts: {
    folder?: string;
    search?: string;
    status?: AssetStatus;
    has_notes?: boolean;
    /** `document` ou `packaging`: qual miolo do viewer o arquivo abre. */
    viewer_kind?: ViewerKind;
    recursive?: boolean;
    sort?: AssetSortOrder;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }): Promise<AssetPage> {
    const params = new URLSearchParams();
    if (opts.folder !== undefined) params.set("folder", opts.folder);
    if (opts.search) params.set("search", opts.search);
    if (opts.status) params.set("status", opts.status);
    if (opts.has_notes !== undefined) params.set("has_notes", String(opts.has_notes));
    if (opts.viewer_kind) params.set("viewer_kind", opts.viewer_kind);
    if (opts.recursive !== undefined) params.set("recursive", String(opts.recursive));
    if (opts.sort) params.set("sort", opts.sort);
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts.offset !== undefined) params.set("offset", String(opts.offset));

    const res = await apiFetch(
      `${API_BASE}/assets?${params}`,
      { signal: opts.signal },
      { timeoutMs: POLL_TIMEOUT_MS },
    );
    const items = await jsonOrThrow<AssetListItem[]>(res);
    // Ausente quando há filtro de status: o backend não consegue um total exato
    // em SQL e prefere omitir a contar errado.
    const rawTotal = res.headers.get("X-Total-Count");
    const total = rawTotal === null ? null : Number.parseInt(rawTotal, 10);
    return { items, total: Number.isFinite(total as number) ? (total as number) : null };
  },

  getAssetHistory(assetId: string): Promise<AssetEvent[]> {
    return apiFetch(`${API_BASE}/assets/${assetId}/history`).then((r) =>
      jsonOrThrow<AssetEvent[]>(r),
    );
  },

  getRenderLogs(
    fileId: string,
    opts?: {
      limit?: number;
      level?: RenderLogItem["level"];
      task_type?: string;
      render_id?: string;
    },
  ): Promise<RenderLogItem[]> {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.level) params.set("level", opts.level);
    if (opts?.task_type) params.set("task_type", opts.task_type);
    if (opts?.render_id) params.set("render_id", opts.render_id);
    const query = params.toString();
    return apiFetch(`${API_BASE}/files/${fileId}/render-logs${query ? `?${query}` : ""}`).then((r) =>
      jsonOrThrow<RenderLogItem[]>(r),
    );
  },

  getNotesPreview(
    assetId: string,
    page = 1,
    limit = 50,
  ): Promise<NotesPreviewResponse> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    return apiFetch(`${API_BASE}/assets/${assetId}/notes-preview?${params}`).then((r) =>
      jsonOrThrow<NotesPreviewResponse>(r),
    );
  },

  listNotes(assetId: string, opts?: { page?: number; limit?: number; offset?: number }): Promise<Note[]> {
    const params = new URLSearchParams();
    if (opts?.page !== undefined) params.set("page", String(opts.page));
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
    const query = params.toString();
    const url = query
      ? `${API_BASE}/assets/${assetId}/notes/?${query}`
      : `${API_BASE}/assets/${assetId}/notes/`;
    return apiFetch(url).then((r) => jsonOrThrow<Note[]>(r));
  },

  createNote(assetId: string, payload: NoteCreateInput): Promise<Note> {
    return apiFetch(`${API_BASE}/assets/${assetId}/notes/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((r) => jsonOrThrow<Note>(r));
  },

  updateNote(assetId: string, noteId: string, payload: NoteUpdateInput): Promise<Note> {
    return apiFetch(`${API_BASE}/assets/${assetId}/notes/${noteId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((r) => jsonOrThrow<Note>(r));
  },

  deleteNote(assetId: string, noteId: string): Promise<Note> {
    return apiFetch(`${API_BASE}/assets/${assetId}/notes/${noteId}`, {
      method: "DELETE",
    }).then((r) => jsonOrThrow<Note>(r));
  },

  listPublicViewNotes(
    token: string,
    opts?: { page?: number; limit?: number; offset?: number; assetId?: string | null },
  ): Promise<Note[]> {
    const params = new URLSearchParams();
    if (opts?.page !== undefined) params.set("page", String(opts.page));
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
    // O lado, no link de comparação: as notas são do arquivo, e o par tem dois.
    // No unitário o backend ignora o parâmetro.
    if (opts?.assetId) params.set("asset_id", opts.assetId);
    const query = params.toString();
    const url = query
      ? `${API_BASE}/public-views/${token}/notes/?${query}`
      : `${API_BASE}/public-views/${token}/notes/`;
    return apiFetch(url, undefined, { public: true }).then((r) => jsonOrThrow<Note[]>(r));
  },

  /**
   * As notas de um lado de uma visualização exportada — leitura, e só (F-S4).
   *
   * Prefixo próprio, e não o `publicViewToken`: aquele monta
   * `/api/public-views/{token}/...`, que responde 404 para um token de link
   * exportado. Cada família de token tem o seu caminho. Não há `create`,
   * `update` nem `delete` desta família, de propósito.
   */
  listSharedViewNotes(
    token: string,
    assetId: string,
    opts?: { page?: number; limit?: number; offset?: number },
  ): Promise<Note[]> {
    const params = new URLSearchParams();
    if (opts?.page !== undefined) params.set("page", String(opts.page));
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
    const query = params.toString();
    const base = `${API_BASE}/viewer/v1/shared-views/${encodeURIComponent(token)}/assets/${assetId}/notes/`;
    return apiFetch(query ? `${base}?${query}` : base, undefined, { public: true }).then((r) =>
      jsonOrThrow<Note[]>(r),
    );
  },

  createPublicViewNote(token: string, payload: NoteCreateInput, assetId?: string | null): Promise<Note> {
    const query = assetId ? `?asset_id=${encodeURIComponent(assetId)}` : "";
    return apiFetch(`${API_BASE}/public-views/${token}/notes/${query}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { public: true }).then((r) => jsonOrThrow<Note>(r));
  },

  updatePublicViewNote(token: string, noteId: string, payload: NoteUpdateInput): Promise<Note> {
    return apiFetch(`${API_BASE}/public-views/${token}/notes/${noteId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { public: true }).then((r) => jsonOrThrow<Note>(r));
  },

  deletePublicViewNote(token: string, noteId: string): Promise<Note> {
    return apiFetch(`${API_BASE}/public-views/${token}/notes/${noteId}`, {
      method: "DELETE",
    }, { public: true }).then((r) => jsonOrThrow<Note>(r));
  },


  async uploadFile(
    file: File,
    options?: {
      path?: string;
      /** O nome no multipart; o envio de pasta manda em NFC (`lib/folderUpload.ts`). */
      filename?: string;
      onProgress?: (progress: UploadProgress) => void;
    },
  ): Promise<FileListItem> {
    const fd = new FormData();
    if (options?.filename) fd.append("upload", file, options.filename);
    else fd.append("upload", file);
    if (options?.path !== undefined) fd.append("path", options.path);
    const token = getAccessToken();
    try {
      return await uploadFormDataWithProgress<FileListItem>(
        `${API_BASE}/files/upload`,
        fd,
        options?.onProgress,
        token,
      );
    } catch (err) {
      if (err instanceof XhrHttpError && err.status === 401) {
        // A mesma regra do `apiFetch` (T-57): só limpa quando havia token.
        const saida = await recoverFrom401({
          accessToken: token,
          refreshToken: getRefreshToken(),
          refresh: _doRefresh,
          clearTokens: clearSessionTokens,
          announceExpired: announceSessionExpired,
        });
        if (saida.kind !== "retry") throw new Error(mensagemDo401(saida.kind));
        return uploadFormDataWithProgress<FileListItem>(
          `${API_BASE}/files/upload`,
          fd,
          options?.onProgress,
          saida.accessToken,
        );
      }
      throw err;
    }
  },

  /**
   * Baixa o original protegido pela autenticação da API.
   * O backend faz stream do arquivo; o frontend cria um Object URL temporário
   * para acionar o download sem expor hostnames internos do storage.
   *
   * `filename` é o nome que a listagem já tem daquele arquivo, e serve de
   * reserva para quando o `Content-Disposition` não chega ao JavaScript
   * (chamada de outra origem). Antes a reserva era a constante `"arquivo.pdf"`:
   * o original perdia o nome e um JPEG era salvo com extensão de PDF.
   */
  async downloadFile(fileId: string, filename?: string | null): Promise<void> {
    const res = await apiFetch(`${API_BASE}/files/${fileId}/download`);
    await saveResponseAsDownload(res, filename ?? "");
  },

  /**
   * Exporta a página atual do viewer como JPEG (rota de administrador).
   *
   * `channels` é a seleção do Painel Técnico já sem os canais técnicos; o
   * servidor refaz esse filtro de qualquer jeito. Omitir a lista significa
   * "todos os canais de arte", que é o caso de uma arte raster, sem separação.
   * O composite sai na densidade em que a página foi rendida — não há
   * parâmetro de resolução aqui de propósito.
   */
  async exportViewerPageJpeg(
    renderId: string,
    options: { page: number; channels?: string[]; filename?: string | null },
  ): Promise<void> {
    const params = new URLSearchParams({ page: String(options.page) });
    if (options.channels?.length) params.set("channels", options.channels.join(","));
    const res = await apiFetch(`${API_BASE}/viewer/v1/renders/${renderId}/export.jpg?${params}`);
    // O nome bom vem do servidor (ele numera a página); `filename` é a reserva
    // do caso cross-origin, e «pagina.jpg» é o que sobra sem nome de origem.
    await saveResponseAsDownload(res, comExtensao(options.filename, "jpg") || "pagina.jpg");
  },

  /**
   * Baixa o relatório de anotações em PDF — o «Exportar relatório» do painel
   * de comentários (set/2026).
   *
   * Duas portas para o mesmo PDF: o link público (o token é a credencial, e a
   * chave `notes_report` é o portão) e a sessão. Quem escolhe é a presença do
   * token, como em todo o resto do painel. O nome bom vem do servidor no
   * `Content-Disposition` — que está em `CORS_EXPOSE_HEADERS`, senão o
   * cross-origin o esconderia e o arquivo sairia com o UUID do blob.
   *
   * Documento (set/2026): página anotada ainda sem raster volta como 409 com o
   * código de «páginas em preparo» — o servidor já as pediu ao worker. Aqui
   * se espera e se pede de novo, contando o andamento em `onPreparing`, até o
   * PDF chegar ou `PRAZO_DA_ESPERA_MS` passar; aí vale a mensagem do servidor.
   */
  async exportNotesReport(options: {
    assetId?: string | null;
    publicViewToken?: string | null;
    filename?: string | null;
    onPreparing?: (preparo: PaginasEmPreparo) => void;
  }): Promise<void> {
    const url = options.publicViewToken
      ? `${API_BASE}/public-views/${encodeURIComponent(options.publicViewToken)}/notes-report.pdf`
      : `${API_BASE}/assets/${options.assetId}/notes-report.pdf`;
    const res = await pedirComEspera(
      () => apiFetch(url, undefined, options.publicViewToken ? { public: true } : undefined),
      { onPreparing: options.onPreparing },
    );
    await saveResponseAsDownload(res, comExtensao(options.filename, "pdf") || "relatorio.pdf");
  },

  getViewerV1BySlug(
    slug: string,
    options?: { poll?: boolean; view?: string | null; variant?: SeparationVariant },
  ): Promise<ViewerV1BySlug> {
    const params = new URLSearchParams({ _: String(Date.now()) });
    if (options?.poll === true) params.set("poll", "1");
    // O token do link de compartilhamento (F-S6, A2): é o contexto de quem
    // abriu `/v/{slug}?view=…` sem sessão.
    if (options?.view) params.set("view", options.view);
    // A VIEW da peça de separações (F-T.14). O LEN é o padrão e não vai.
    if (options?.variant === "view") params.set("variant", "view");
    return apiFetch(`${API_BASE}/viewer/v1/by-slug/${slug}?${params}`).then((r) =>
      jsonOrThrow<ViewerV1BySlug>(r),
    );
  },

  getPublicView(token: string, options?: { poll?: boolean; variant?: SeparationVariant }): Promise<PublicViewByToken> {
    const params = new URLSearchParams({ _: String(Date.now()) });
    if (options?.poll === true) params.set("poll", "1");
    if (options?.variant === "view") params.set("variant", "view");
    return apiFetch(`${API_BASE}/public-views/${token}?${params}`, undefined, { public: true }).then((r) =>
      jsonOrThrow<PublicViewByToken>(r),
    );
  },

  /**
   * Começa o teste grátis do Viewer Pro da empresa do link (out/2026). Devolve
   * o bloco `public_view` já com o plano do teste; 403 sem oferta, 409 depois
   * do fim do teste da empresa.
   */
  startPublicProTrial(token: string): Promise<{ public_view: PublicViewByToken["public_view"] }> {
    return apiFetch(`${API_BASE}/public-views/${token}/pro-trial`, { method: "POST" }, { public: true }).then((r) =>
      jsonOrThrow<{ public_view: PublicViewByToken["public_view"] }>(r),
    );
  },

  /**
   * A sessão de comparação de um link público de par — o `compare-sessions`
   * com o token no lugar dos slugs. Sondada como a outra, até os dois lados
   * ficarem prontos; `poll: true` nas sondagens, para não contar abertura.
   */
  createPublicCompareSession(
    token: string,
    payload: {
      poll?: boolean;
      /** Troca os papéis: o par B-contra-A do modo A + B. */
      inverted?: boolean;
      page_a?: number;
      page_b?: number;
      channels?: string[];
      difference_threshold?: number;
      offset_x?: number;
      offset_y?: number;
      offset_mm_x?: number;
      offset_mm_y?: number;
      reference_side?: "a" | "b";
      red_side?: "left" | "right";
      rotate_moving?: 0 | 90 | 180 | 270;
      /** O mapa de tintas, nos termos do pedido (com `inverted`, já espelhado). */
      channel_map?: ChannelMapEntry[];
      /** F-CP.9 — a caixa «Comparar por separação de cores»; ausente vale o perfil do link. */
      pdf_pair_engine?: "channels" | "composite";
    },
  ): Promise<PublicCompareSession> {
    return apiFetch(`${API_BASE}/public-views/${encodeURIComponent(token)}/compare-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page_a: 1,
        page_b: 1,
        offset_x: 0,
        offset_y: 0,
        ...payload,
      }),
    }, { public: true }).then((r) => jsonOrThrow<PublicCompareSession>(r));
  },

  getViewerV1Render(renderId: string): Promise<ViewerV1Render> {
    return apiFetch(`${API_BASE}/viewer/v1/renders/${renderId}`).then((r) =>
      jsonOrThrow<ViewerV1Render>(r),
    );
  },

  getViewerV1TileSource(renderId: string): Promise<ViewerV1TileSource> {
    return apiFetch(`${API_BASE}/viewer/v1/renders/${renderId}/tile-source`).then((r) =>
      jsonOrThrow<ViewerV1TileSource>(r),
    );
  },

  getViewerV1Document(renderId: string, signal?: AbortSignal): Promise<ViewerDocumentBootstrap> {
    return apiFetch(`${API_BASE}/viewer/v1/renders/${encodeURIComponent(renderId)}/document`, { signal }).then((r) =>
      jsonOrThrow<ViewerDocumentBootstrap>(r),
    );
  },

  getViewerV1DocumentSemantics(
    renderId: string,
    page: number,
    signal?: AbortSignal,
  ): Promise<ViewerDocumentSemantics> {
    return apiFetch(
      `${API_BASE}/viewer/v1/renders/${encodeURIComponent(renderId)}/document/pages/${page}/semantics`,
      { signal },
    ).then((r) => jsonOrThrow<ViewerDocumentSemantics>(r));
  },

  searchViewerV1Document(
    renderId: string,
    query: string,
    options?: { offset?: number; limit?: number; signal?: AbortSignal },
  ): Promise<ViewerDocumentSearchResponse> {
    const params = new URLSearchParams({ q: query });
    if (options?.offset !== undefined) params.set("offset", String(options.offset));
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    return apiFetch(`${API_BASE}/viewer/v1/renders/${encodeURIComponent(renderId)}/document/search?${params}`, {
      signal: options?.signal,
    }).then((r) => jsonOrThrow<ViewerDocumentSearchResponse>(r));
  },

  /**
   * O diff semântico de `left` («antes») para `right` («depois»), por slug.
   *
   * O par é o endereço: o servidor cacheia por identidade dos dois lados, e
   * re-renderizar qualquer um deles troca a identidade e invalida sozinho.
   */
  /** As versões do asset, da mais nova para a mais antiga. */
  listAssetVersions(assetId: string): Promise<AssetVersionItem[]> {
    return apiFetch(`${API_BASE}/assets/${encodeURIComponent(assetId)}/versions`).then((r) =>
      jsonOrThrow<AssetVersionItem[]>(r),
    );
  },

  getDocumentCompare(
    source: DocumentCompareSource,
    signal?: AbortSignal,
    mode: "changes" | "conference" = "changes",
  ): Promise<DocumentCompare> {
    if (isMixedDocumentSource(source)) {
      // Documento × arte não tem diff global: a arte não tem seções para
      // parear com a especificação. O que existe é a conferência por
      // seleção; o painel recebe um diff vazio, em modo conferência, sem
      // nenhuma requisição.
      const slugs = source.kind === "mixed-slug" ? { left: source.left, right: source.right } : { left: "", right: "" };
      return Promise.resolve({
        schema_version: "document_conference_v1",
        algorithm_version: "linkflow-document-conference-v5",
        stats: { blocks: 0, unchanged: 0, modified: 0, added: 0, removed: 0, changed: 0, spans: 0, tags: {} },
        blocks: [],
        truncated: false,
        cached: true,
        left: { slug: slugs.left, filename: source.leftName ?? "", cache_identity: "" },
        right: { slug: slugs.right, filename: source.rightName ?? "", cache_identity: "" },
        mode: "conference",
        roles: { a: "spec", b: "arte", reference: "a" },
      });
    }
    if (source.kind === "public-pair") {
      const params = new URLSearchParams({ mode });
      if (source.inverted) params.set("inverted", "true");
      return apiFetch(
        `${API_BASE}/public-views/${encodeURIComponent(source.token)}/document-compare?${params}`,
        { signal },
      ).then((r) => jsonOrThrow<DocumentCompare>(r));
    }
    const params = new URLSearchParams({ left: source.left, right: source.right, mode });
    return apiFetch(`${API_BASE}/viewer/v1/document-compares/by-slug?${params}`, {
      signal,
    }).then((r) => jsonOrThrow<DocumentCompare>(r));
  },

  /**
   * O que mudou no TEXTO da arte A para a arte B, sobre a leitura do Vision
   * de cada uma (a do Modo Texto). Nunca dispara leitura: sem ela nos dois
   * lados a resposta é `state: "sem_leitura"`, com o pé de cada lado.
   */
  getTextCompare(
    input: {
      left: string;
      right: string;
      pageA: number;
      pageB: number;
      /** A área comum do alinhamento (corta só com `crop`); sem ela, a página inteira. */
      area?: import("@/lib/compareText").TextCompareArea | null;
    },
    signal?: AbortSignal,
  ): Promise<import("@/lib/compareText").PackagingTextCompare> {
    const params = new URLSearchParams({
      left: input.left,
      right: input.right,
      page_a: String(input.pageA),
      page_b: String(input.pageB),
    });
    if (input.area) {
      params.set("area_a", input.area.a.join(","));
      params.set("area_b", input.area.b.join(","));
      params.set("turn_a", String(input.area.turnA));
      params.set("turn_b", String(input.area.turnB));
      if (input.area.crop) params.set("crop", "true");
      if (input.area.beyond === false) params.set("beyond", "false");
    }
    return apiFetch(`${API_BASE}/viewer/v1/text-compares/by-slug?${params}`, { signal }).then((r) =>
      jsonOrThrow<import("@/lib/compareText").PackagingTextCompare>(r),
    );
  },

  /** «Este trecho confere no outro documento?» — a conferência por seleção. */
  locateDocumentSelection(
    source: DocumentCompareSource,
    input: {
      side: "a" | "b";
      page: number;
      start: number;
      end: number;
      /** Só no par documento × arte: as páginas da arte em que procurar (1-based). */
      pagesB?: number[] | null;
    },
    signal?: AbortSignal,
  ): Promise<DocumentLocateResult> {
    const comuns: Record<string, string> = {
      side: input.side,
      page: String(input.page),
      start: String(input.start),
      end: String(input.end),
    };
    if (isMixedDocumentSource(source) && input.pagesB?.length) {
      comuns.pages_b = input.pagesB.join(",");
    }
    if (source.kind === "mixed-pair") {
      // O servidor sabe qual lado do link é o documento: `side=a` é sempre ele.
      return apiFetch(
        `${API_BASE}/public-views/${encodeURIComponent(source.token)}/document-compare/locate?${new URLSearchParams(comuns)}`,
        { signal },
      ).then((r) => jsonOrThrow<DocumentLocateResult>(r));
    }
    if (source.kind === "public-pair") {
      const params = new URLSearchParams(comuns);
      if (source.inverted) params.set("inverted", "true");
      return apiFetch(
        `${API_BASE}/public-views/${encodeURIComponent(source.token)}/document-compare/locate?${params}`,
        { signal },
      ).then((r) => jsonOrThrow<DocumentLocateResult>(r));
    }
    const params = new URLSearchParams({ left: source.left, right: source.right, ...comuns });
    return apiFetch(`${API_BASE}/viewer/v1/document-compares/locate?${params}`, {
      signal,
    }).then((r) => jsonOrThrow<DocumentLocateResult>(r));
  },

  /**
   * A varredura de números de uma seleção (fase DA/I4): cada número com
   * unidade, telefone, CNPJ, data ou código da seleção é conferido na arte.
   * Só no par documento × arte.
   */
  sweepNumbers(
    source: DocumentCompareSource,
    input: { page: number; start: number; end: number; pagesB?: number[] | null },
    signal?: AbortSignal,
  ): Promise<NumberSweepResult> {
    if (!isMixedDocumentSource(source)) {
      return Promise.reject(new Error("A varredura de números existe só no par documento × arte."));
    }
    const params = new URLSearchParams({
      page: String(input.page),
      start: String(input.start),
      end: String(input.end),
    });
    if (input.pagesB?.length) params.set("pages_b", input.pagesB.join(","));
    if (source.kind === "mixed-pair") {
      return apiFetch(
        `${API_BASE}/public-views/${encodeURIComponent(source.token)}/document-compare/number-sweep?${params}`,
        { signal },
      ).then((r) => jsonOrThrow<NumberSweepResult>(r));
    }
    params.set("left", source.left);
    params.set("right", source.right);
    return apiFetch(`${API_BASE}/viewer/v1/document-compares/number-sweep?${params}`, { signal }).then((r) =>
      jsonOrThrow<NumberSweepResult>(r),
    );
  },

  /**
   * A busca reversa (fase DA/I3): um texto da ARTE procurado na especificação.
   * Devolve todas as ocorrências; com mais de uma, quem aponta é o revisor.
   */
  locateInDocument(
    source: DocumentCompareSource,
    input: { text: string; pageB: number },
    signal?: AbortSignal,
  ): Promise<DocumentLocateResult> {
    if (!isMixedDocumentSource(source)) {
      return Promise.reject(new Error("A busca na especificação existe só no par documento × arte."));
    }
    const params = new URLSearchParams({ text: input.text, page_b: String(input.pageB) });
    if (source.kind === "mixed-pair") {
      return apiFetch(
        `${API_BASE}/public-views/${encodeURIComponent(source.token)}/document-compare/locate-reverse?${params}`,
        { signal },
      ).then((r) => jsonOrThrow<DocumentLocateResult>(r));
    }
    params.set("left", source.left);
    params.set("right", source.right);
    return apiFetch(`${API_BASE}/viewer/v1/document-compares/locate-reverse?${params}`, { signal }).then((r) =>
      jsonOrThrow<DocumentLocateResult>(r),
    );
  },

  createViewerV1CompareSession(payload: {
    file_a_id?: string;
    file_b_id?: string;
    slug_a?: string;
    slug_b?: string;
    /** O token do link de compartilhamento (`?view=`), F-S6 (A2). */
    view?: string | null;
    page_a?: number;
    page_b?: number;
    channels?: string[];
    difference_threshold?: number;
    offset_x?: number;
    offset_y?: number;
    offset_mm_x?: number;
    offset_mm_y?: number;
    /** Força a âncora do diff. Só afeta comparação raster. */
    reference_side?: "a" | "b";
    red_side?: "left" | "right";
    /** Um quarto de volta aplicado só ao Item B, antes de comparar. */
    rotate_moving?: 0 | 90 | 180 | 270;
    /**
     * O mapa de tintas (rflow 2.3.5). Ausente, o servidor aplica a proposta
     * dele; uma lista, mesmo vazia, é a escolha de quem pediu.
     */
    channel_map?: ChannelMapEntry[];
    /**
     * Reenfileira um render que já falhou.
     *
     * Só o botão «Tentar novamente» manda `true`: a sondagem normal não pode,
     * senão a mesma arte quebrada volta à fila de dois em dois segundos e a
     * espera não termina nunca.
     */
    retry_failed?: boolean;
    /**
     * O perfil de comparação pelo nome (F-CP.3): `default` ou um perfil de
     * teste. Só com sessão — anônimo recebe 400; nome desconhecido, 422.
     * Ausente é o padrão.
     */
    profile?: string;
    /**
     * F-CP.9 — o motor do par PDF × PDF, a caixa «Comparar por separação de
     * cores»: `channels` (marcada) ou `composite` (desmarcada). Aceito de
     * anônimo (é vista). Ausente vale o perfil; a tela manda sempre.
     */
    pdf_pair_engine?: "channels" | "composite";
    /**
     * F-CA.4 — a combinação de camadas de cada lado, na sintaxe `layers=` do
     * viewer (`on:10.0,11.0`). `layers_a` é sempre do `render_a` desta chamada:
     * no par invertido, a do Item B. Só com sessão, e só quando não é o padrão
     * (a tela nunca manda `null`): anônimo com valor leva 400; motor por
     * separação e página sem chapas parciais, 409 com `detail.code`.
     */
    layers_a?: string;
    layers_b?: string;
  }): Promise<ViewerV1CompareSession> {
    return apiFetch(`${API_BASE}/viewer/v1/compare-sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page_a: 1,
        page_b: 1,
        offset_x: 0,
        offset_y: 0,
        ...payload,
      }),
    }).then((r) => jsonOrThrow<ViewerV1CompareSession>(r));
  },

  /**
   * O padrão e os perfis de teste, para o seletor «Perfil» (F-CP.3). Exige
   * sessão; `threshold` vem nulo (o limiar só se resolve contra um par).
   */
  listViewerV1CompareProfiles(signal?: AbortSignal): Promise<{ profiles: CompareProfileListItem[] }> {
    return apiFetch(`${API_BASE}/viewer/v1/compare-profiles`, { signal }).then((r) =>
      jsonOrThrow<{ profiles: CompareProfileListItem[] }>(r),
    );
  },

  refineViewerV1CompareAlignment(
    compareId: string,
    start: {
      offset_x?: number;
      offset_y?: number;
      offset_mm_x?: number;
      offset_mm_y?: number;
      scale: number;
      rotate_moving?: number;
    },
  ): Promise<ViewerV1AutoAlignResult> {
    return apiFetch(`${API_BASE}/viewer/v1/compares/${compareId}/refine-alignment`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(start),
    }).then((r) => jsonOrThrow<ViewerV1AutoAlignResult>(r));
  },

  /**
   * Congela a tela atual num link com prazo.
   *
   * `state` é opaco aqui de propósito: quem exporta é a página, que sabe o que
   * a própria tela tem, e quem lê é a mesma página. Tipar o estado neste ponto
   * criaria um terceiro lugar para esquecer de atualizar um campo.
   */
  createSharedView(body: {
    kind: "viewer" | "compare";
    target: Record<string, string>;
    state: Record<string, unknown>;
    ttl_hours?: number;
    // O `id` do retorno é a alça da revogação (F-S2): sem ele, o único que
    // conhecia a linha era quem **recebia** o link, e quem exportou não tinha
    // como cortar.
  }): Promise<{ id: string; token: string; path: string; expires_at: string; kind: string }> {
    return apiFetch(`${API_BASE}/viewer/v1/shared-views`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow(r));
  },

  /** Corta uma visualização exportada antes do vencimento. Idempotente. */
  revokeSharedView(sharedViewId: string): Promise<void> {
    return apiFetch(`${API_BASE}/viewer/v1/shared-views/${encodeURIComponent(sharedViewId)}`, {
      method: "DELETE",
    }).then((r) => {
      if (!r.ok) throw new Error(`Falha ao revogar a visualização (${r.status})`);
    });
  },

  getSharedView(token: string): Promise<{
    kind: "viewer" | "compare";
    target: Record<string, string>;
    state: Record<string, unknown>;
    expires_at: string;
    /** A capability do link (F-S6), com o prazo do próprio link. */
    url_capability?: UrlCapability | null;
  }> {
    return apiFetch(`${API_BASE}/viewer/v1/shared-views/${encodeURIComponent(token)}`).then((r) =>
      jsonOrThrow(r),
    );
  },

  autoAlignViewerV1Compare(compareId: string, rotateMoving = 0): Promise<ViewerV1AutoAlignResult> {
    const query = rotateMoving ? `?rotate_moving=${rotateMoving}` : "";
    return apiFetch(`${API_BASE}/viewer/v1/compares/${compareId}/auto-align${query}`, {
      method: "POST",
    }).then((r) => jsonOrThrow<ViewerV1AutoAlignResult>(r));
  },

  /**
   * Alinha localizando um objeto do Item A no Item B.
   *
   * `box` está em pixels base do Item A, onde o usuário desenhou. `box_b` é o
   * MESMO objeto marcado no Item B, **em pixels da vista dele** (girada por
   * `rotate_moving`) — é a segunda marca de E5.2, e é ela que troca o prior de
   * escala da razão mm/px do par pela razão medida entre as duas marcas. Sem
   * ela o par de artes com painéis de tamanhos físicos diferentes devolve o
   * casamento errado; com ela, o encaixe correto. Continua sendo opcional: para
   * duas versões da mesma peça, uma marca só é o caminho certo.
   */
  objectAlignViewerV1Compare(
    compareId: string,
    box: {
      x: number;
      y: number;
      width: number;
      height: number;
      rotate_moving?: number;
      box_b?: { x: number; y: number; width: number; height: number };
    },
  ): Promise<ViewerV1ObjectAlignResult> {
    return apiFetch(`${API_BASE}/viewer/v1/compares/${compareId}/object-align`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(box),
    }).then((r) => jsonOrThrow<ViewerV1ObjectAlignResult>(r));
  },

  getViewerV1CompareDifferenceRegions(
    compareId: string,
    options: {
      offsetX?: number;
      offsetY?: number;
      offsetMmX?: number;
      offsetMmY?: number;
      differenceThreshold: number;
      scale?: number;
      rotateMoving?: number;
      solidThreshold?: number;
      granularity?: "fine" | "medium" | "coarse";
      /** E7.2 — omitido em `appearance`, que é o padrão de todo compare já em cache. */
      mode?: string;
      /** F-CP.1/F-CP.2b — só quando difere do gravado no compare; ausente vale o gravado. */
      registrationToleranceMm?: number;
      /** F-CP.2b — o piso da lista em mm²; só quando difere do padrão de 0,5. */
      minAreaMm2?: number;
      /**
       * F-CP.4 — o perfil cujas réguas de granulometria valem; só quando não é
       * o padrão (o servidor recusa outro nome sem sessão).
       */
      profile?: string;
      /** F-CP.7 — o filtro de ruído do lado imagem; só quando não é `none`. */
      noiseFilter?: string;
      /**
       * F-CP.10 — as réguas da posição em uso (`granularity`), em mm, por cima
       * das do perfil; só as que diferem dele (o servidor as recusa sem sessão).
       */
      dilationMm?: number;
      fusionMm?: number;
      capMm?: number;
      /**
       * F-CP.8b — o token do link público: o servidor usa as réguas do perfil
       * do link; nunca junto com `profile`.
       */
      publicView?: string;
      /**
       * F-CP.5 — limiar e área mínima por classe; só os que diferem do par
       * (`classParamsForRequest`). Anônimo pode mandar: são vista.
       */
      thresholdText?: number;
      thresholdArt?: number;
      minAreaTextMm2?: number;
      minAreaArtMm2?: number;
    },
    signal?: AbortSignal,
  ): Promise<ViewerV1DifferenceRegions> {
    const query = new URLSearchParams({
      difference_threshold: String(options.differenceThreshold),
      scale: String(options.scale ?? 1),
    });
    if (options.rotateMoving) query.set("rotate_moving", String(options.rotateMoving));
    if (options.mode && options.mode !== "appearance") query.set("mode", options.mode);
    if (options.solidThreshold) query.set("solid_threshold", String(options.solidThreshold));
    if (options.granularity) query.set("granularity", options.granularity);
    if (options.offsetMmX !== undefined || options.offsetMmY !== undefined) {
      query.set("offset_mm_x", String(options.offsetMmX ?? 0));
      query.set("offset_mm_y", String(options.offsetMmY ?? 0));
    } else {
      query.set("offset_x", String(options.offsetX ?? 0));
      query.set("offset_y", String(options.offsetY ?? 0));
    }
    // No fim da query, e só quando vêm: o pedido de quem nunca mudou nada
    // continua com a mesma URL de antes.
    if (options.registrationToleranceMm !== undefined) {
      query.set("registration_tolerance_mm", String(options.registrationToleranceMm));
    }
    if (options.minAreaMm2 !== undefined) query.set("min_area_mm2", String(options.minAreaMm2));
    if (options.profile) query.set("profile", options.profile);
    if (options.noiseFilter && options.noiseFilter !== "none") query.set("noise_filter", options.noiseFilter);
    if (options.dilationMm !== undefined) query.set("granularity_dilation_mm", String(options.dilationMm));
    if (options.fusionMm !== undefined) query.set("granularity_fusion_mm", String(options.fusionMm));
    if (options.capMm !== undefined) query.set("granularity_cap_mm", String(options.capMm));
    if (options.publicView) query.set("public_view", options.publicView);
    if (options.thresholdText !== undefined) query.set("threshold_text", String(options.thresholdText));
    if (options.thresholdArt !== undefined) query.set("threshold_art", String(options.thresholdArt));
    if (options.minAreaTextMm2 !== undefined) query.set("min_area_text_mm2", String(options.minAreaTextMm2));
    if (options.minAreaArtMm2 !== undefined) query.set("min_area_art_mm2", String(options.minAreaArtMm2));
    return apiFetch(`${API_BASE}/viewer/v1/compares/${compareId}/difference-regions?${query}`, { signal }).then(
      async (r) => {
        // F-CP.12i: a máscara nativa desta identidade ainda não existe — o
        // corpo é o estado do job, e quem pediu espera a máscara ficar pronta.
        await throwIfMaskPending(r);
        return jsonOrThrow<ViewerV1DifferenceRegions>(r);
      },
    );
  },

  /**
   * F-CP.12i — o estado da máscara nativa do compare para os parâmetros dados
   * (`maskQueryFor`): `pending|running|ready|error`, com o progresso em tiles.
   * Identidade sem máscara dispara o job no servidor. Sempre 200.
   */
  getViewerV1CompareMaskStatus(
    compareId: string,
    query: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<CompareMaskStatus> {
    const params = new URLSearchParams(query);
    return apiFetch(`${API_BASE}/viewer/v1/compares/${compareId}/mask-status?${params}`, { signal }).then((r) =>
      jsonOrThrow<CompareMaskStatus>(r),
    );
  },

  /**
   * F-CP.12i — o «tentar de novo»: apaga a identidade que falhou (só em erro
   * ou morta pela TTL; um job vivo fica como está) e dispara o job outra vez.
   * A resposta é o estado novo.
   */
  retryViewerV1CompareMask(compareId: string, query: Record<string, string>): Promise<CompareMaskStatus> {
    const params = new URLSearchParams(query);
    return apiFetch(`${API_BASE}/viewer/v1/compares/${compareId}/mask-retry?${params}`, { method: "POST" }).then((r) =>
      jsonOrThrow<CompareMaskStatus>(r),
    );
  },

  async fetchViewerV1Image(url: string, signal?: AbortSignal): Promise<ViewerV1ImageResponse> {
    const started = performance.now();
    const response = await apiFetch(url, { signal });
    // F-CP.12i: 202 é «a máscara desta identidade ainda não existe», com o
    // estado do job no corpo — nunca uma imagem. Tratá-lo como `ok` fazia o
    // JSON virar blob, a decodificação falhar e a tela pintar o véu vermelho.
    await throwIfMaskPending(response);
    // Sem isto o corpo cru do FastAPI (`{"detail":"..."}`) virava a mensagem
    // exibida na tela do viewer e do compare.
    if (!response.ok) throw await apiErrorFromResponse(response);
    const blob = await response.blob();
    return { blob, headers: response.headers, elapsedMs: performance.now() - started };
  },

  /**
   * Trigger a render with the given mode ("channels" | "composite").
   *
   * `layers: true` é o «Renderizar com camadas» do manager: o render lê as
   * camadas OCG e corta as chapas parciais. Sem ele o render é o comum — e
   * desliga as camadas de um arquivo que as tinha.
   */
  renderFile(
    id: string,
    mode: "channels" | "composite",
    options: { layers?: boolean } = {},
  ): Promise<{ file_id: string; status: string; mode: string; layers?: boolean; pipeline?: string; render_id?: string; job_id?: string }> {
    const layers = options.layers ? "&layers=true" : "";
    return apiFetch(`${API_BASE}/files/${id}/render?mode=${mode}${layers}`, { method: "POST" }).then((r) =>
      jsonOrThrow<{ file_id: string; status: string; mode: string; pipeline?: string; render_id?: string; job_id?: string }>(r),
    );
  },

  /** Troca o viewer do arquivo (`auto` devolve à triagem) — ver `viewerKindOverride.ts`. */
  setViewerKind(
    fileId: string,
    kind: import("./viewerKindOverride").ViewerKindChoice,
  ): Promise<import("./viewerKindOverride").ViewerKindChange> {
    return apiFetch(`${API_BASE}/files/${fileId}/viewer-kind`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind }),
    }).then((r) => jsonOrThrow<import("./viewerKindOverride").ViewerKindChange>(r));
  },

  // ---------- Barcode detection (F1.13) ----------

  detectBarcodes(
    renderId: string,
    page: number,
    bbox: [number, number, number, number],
    channels: string[] | null,
    publicViewToken?: string | null,
  ): Promise<{ task_id: string }> {
    const rota = toolsRoute(publicViewToken);
    return apiFetch(`${rota.base}/barcode`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ render_id: renderId, page, bbox, channels }),
    }, rota.options).then((r) => jsonOrThrow<{ task_id: string }>(r));
  },

  readBraille(
    renderId: string,
    page: number,
    bbox: [number, number, number, number],
    channels: string[] | null,
    publicViewToken?: string | null,
  ): Promise<{ task_id: string }> {
    const rota = toolsRoute(publicViewToken);
    return apiFetch(`${rota.base}/braille`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ render_id: renderId, page, bbox, channels }),
    }, rota.options).then((r) => jsonOrThrow<{ task_id: string }>(r));
  },

  readOcr(
    renderId: string,
    page: number,
    bbox: [number, number, number, number],
    channels: string[] | null,
    publicViewToken?: string | null,
    /** O idioma escolhido à mão no balão; ausente, o servidor identifica. */
    language?: string | null,
  ): Promise<{ task_id: string }> {
    const rota = toolsRoute(publicViewToken);
    return apiFetch(`${rota.base}/ocr`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ render_id: renderId, page, bbox, channels, ...(language ? { language } : {}) }),
    }, rota.options).then((r) => jsonOrThrow<{ task_id: string }>(r));
  },

  readTableOcr(
    renderId: string,
    page: number,
    bbox: [number, number, number, number],
    channels: string[] | null,
    publicViewToken?: string | null,
  ): Promise<{ task_id: string }> {
    const publicMode = Boolean(publicViewToken);
    const url = publicMode
      ? `${API_BASE}/public-views/${publicViewToken}/tools/table-ocr`
      : `${API_BASE}/tools/table-ocr`;
    return apiFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ render_id: renderId, page, bbox, channels }),
    }, publicMode ? { public: true } : undefined).then((r) => jsonOrThrow<{ task_id: string }>(r));
  },

  // ---------- Google Vision text mode ----------

  startTextAnalysis(renderId: string, publicViewToken?: string | null): Promise<import("@/lib/textAnalysis").ViewerTextAnalysis> {
    const publicMode = Boolean(publicViewToken);
    const url = publicMode
      ? `${API_BASE}/public-views/${publicViewToken}/tools/text-analysis`
      : `${API_BASE}/tools/text-analysis`;
    return apiFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ render_id: renderId }),
    }, publicMode ? { public: true } : undefined).then((r) => jsonOrThrow<import("@/lib/textAnalysis").ViewerTextAnalysis>(r));
  },

  getCurrentTextAnalysis(renderId: string, publicViewToken?: string | null): Promise<import("@/lib/textAnalysis").ViewerTextAnalysis | null> {
    const publicMode = Boolean(publicViewToken);
    const query = new URLSearchParams({ render_id: renderId });
    const url = publicMode
      ? `${API_BASE}/public-views/${publicViewToken}/tools/text-analysis/current?${query}`
      : `${API_BASE}/tools/text-analysis/current?${query}`;
    return apiFetch(url, undefined, publicMode ? { public: true } : undefined).then((r) =>
      jsonOrThrow<import("@/lib/textAnalysis").ViewerTextAnalysis | null>(r),
    );
  },

  getTextAnalysisStatus(
    analysisId: string,
    renderId: string,
    publicViewToken?: string | null,
  ): Promise<import("@/lib/textAnalysis").ViewerTextAnalysis> {
    const publicMode = Boolean(publicViewToken);
    const query = new URLSearchParams({ render_id: renderId });
    const url = publicMode
      ? `${API_BASE}/public-views/${publicViewToken}/tools/text-analysis/${analysisId}?${query}`
      : `${API_BASE}/tools/text-analysis/${analysisId}`;
    return apiFetch(url, undefined, publicMode ? { public: true } : undefined).then((r) =>
      jsonOrThrow<import("@/lib/textAnalysis").ViewerTextAnalysis>(r),
    );
  },

  /**
   * Traduz trechos de uma análise já pronta (F2.12).
   *
   * A `key` de cada item é do cliente e volta como veio — é ela que casa a
   * resposta com o que está na tela. O servidor serve do cache o que já foi
   * traduzido, então repetir o pedido do mesmo parágrafo não paga de novo.
   */
  translateTextAnalysis(
    analysisId: string,
    renderId: string,
    items: { key: string; text: string }[],
    publicViewToken?: string | null,
  ): Promise<{ translations: Record<string, import("@/lib/textAnalysis").TextTranslation> }> {
    const publicMode = Boolean(publicViewToken);
    const url = publicMode
      ? `${API_BASE}/public-views/${publicViewToken}/tools/text-analysis/${analysisId}/translations`
      : `${API_BASE}/tools/text-analysis/${analysisId}/translations`;
    return apiFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ render_id: renderId, items }),
    }, publicMode ? { public: true } : undefined).then((r) =>
      jsonOrThrow<{ translations: Record<string, import("@/lib/textAnalysis").TextTranslation> }>(r),
    );
  },

  getBrailleTaskStatus(taskId: string): Promise<{
    status: string;
    result?: import("@/state/brailleStore").BrailleResult;
    error?: string;
  }> {
    const rota = toolsRoute();
    return apiFetch(`${rota.base}/braille/${taskId}`, undefined, rota.options).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: import("@/state/brailleStore").BrailleResult;
        error?: string;
      }>(r),
    );
  },

  getPublicViewBrailleTaskStatus(publicViewToken: string, taskId: string): Promise<{
    status: string;
    result?: import("@/state/brailleStore").BrailleResult;
    error?: string;
  }> {
    return apiFetch(`${API_BASE}/public-views/${publicViewToken}/tools/braille/${taskId}`, undefined, { public: true }).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: import("@/state/brailleStore").BrailleResult;
        error?: string;
      }>(r),
    );
  },

  getOcrTaskStatus(taskId: string): Promise<{
    status: string;
    result?: import("@/state/ocrStore").OcrResult;
    error?: string;
  }> {
    const rota = toolsRoute();
    return apiFetch(`${rota.base}/ocr/${taskId}`, undefined, rota.options).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: import("@/state/ocrStore").OcrResult;
        error?: string;
      }>(r),
    );
  },

  /**
   * O «Traduzir» do balão do Ler Texto (TR1). Sem corpo: o servidor lê o texto
   * da própria leitura. Com sessão, ou pelo token do link público do Pro
   * (28/10/2026). Não passa por `toolsRoute` porque a comparação exportada
   * continua sem a rota. 404 quando a leitura expirou (o resultado da task
   * vive um dia).
   */
  translateOcrReading(
    taskId: string,
    publicViewToken?: string | null,
  ): Promise<import("@/lib/ocrTranslation").OcrTranslationResponse> {
    const tarefa = encodeURIComponent(taskId);
    const pedido = publicViewToken
      ? apiFetch(
        `${API_BASE}/public-views/${publicViewToken}/tools/ocr/${tarefa}/translation`,
        { method: "POST" },
        { public: true },
      )
      : apiFetch(`${API_BASE}/tools/ocr/${tarefa}/translation`, { method: "POST" });
    return pedido.then((r) => jsonOrThrow<import("@/lib/ocrTranslation").OcrTranslationResponse>(r));
  },

  getPublicViewOcrTaskStatus(publicViewToken: string, taskId: string): Promise<{
    status: string;
    result?: import("@/state/ocrStore").OcrResult;
    error?: string;
  }> {
    return apiFetch(`${API_BASE}/public-views/${publicViewToken}/tools/ocr/${taskId}`, undefined, { public: true }).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: import("@/state/ocrStore").OcrResult;
        error?: string;
      }>(r),
    );
  },

  getTableOcrTaskStatus(taskId: string): Promise<{
    status: string;
    result?: import("@/state/tableOcrStore").TableOcrResult;
    error?: string;
  }> {
    return apiFetch(`${API_BASE}/tools/table-ocr/${taskId}`).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: import("@/state/tableOcrStore").TableOcrResult;
        error?: string;
      }>(r),
    );
  },

  getPublicViewTableOcrTaskStatus(publicViewToken: string, taskId: string): Promise<{
    status: string;
    result?: import("@/state/tableOcrStore").TableOcrResult;
    error?: string;
  }> {
    return apiFetch(`${API_BASE}/public-views/${publicViewToken}/tools/table-ocr/${taskId}`, undefined, { public: true }).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: import("@/state/tableOcrStore").TableOcrResult;
        error?: string;
      }>(r),
    );
  },

  /**
   * Mede lineatura e ângulo da retícula numa janela do nível 0 (F-T.6).
   *
   * `x`/`y` são o **centro** da janela, em pixels do nível 0 — o mesmo espaço em
   * que o canvas trabalha. Não há variante por link público: a ferramenta é do
   * viewer autenticado, como a pipeta era antes de ganhar o proxy.
   */
  /**
   * Mede a retícula de **todas** as tintas na área selecionada (F-T.10, §5).
   *
   * Não há mais `channel`: escolher a tinta antes de medir era pedir ao
   * operador a resposta que ele veio buscar. `channels` restringe às tintas
   * visíveis; sem ele, o conjunto inteiro.
   */
  /**
   * «Medir Texto», motor B (F-MT.1): as palavras do texto vivo da página, com
   * fonte, corpo e alturas. Uma chamada por render e página; o clique é
   * resolvido na tela. Com sessão, pelo link exportado do compare ou pelo
   * link público de viewer do plano Pro em diante (21/10/2026).
   */
  fetchTextMeasureWords(
    renderId: string,
    page: number,
    signal?: AbortSignal,
    publicViewToken?: string | null,
  ): Promise<TextMeasureWordsResult> {
    const params = new URLSearchParams({ render_id: renderId, page: String(page) });
    const rota = toolsRoute(publicViewToken);
    return apiFetch(`${rota.base}/text-measure/words?${params}`, { signal }, rota.options).then((r) =>
      jsonOrThrow<TextMeasureWordsResult>(r),
    );
  },

  /**
   * «Medir Texto», motor C (F-MT.2): os glifos em curva da página, pela caixa
   * exata do caminho, e as linhas pela base. Uma chamada por render e página,
   * como as palavras; pelos mesmos caminhos delas.
   */
  fetchTextMeasureGlyphs(
    renderId: string,
    page: number,
    signal?: AbortSignal,
    publicViewToken?: string | null,
  ): Promise<TextMeasureGlyphsResult> {
    const params = new URLSearchParams({ render_id: renderId, page: String(page) });
    const rota = toolsRoute(publicViewToken);
    return apiFetch(`${rota.base}/text-measure/glyphs?${params}`, { signal }, rota.options).then((r) =>
      jsonOrThrow<TextMeasureGlyphsResult>(r),
    );
  },

  /**
   * «Medir Texto», motor A (F-MT.1): a caixa da tinta na seleção arrastada,
   * em px da base. `bbox` arredondado como em `measureScreenRegion`. A
   * `orientation` (F-MT.2) decide em que eixo o servidor conta os patamares.
   */
  measureTextInk(
    renderId: string,
    page: number,
    bbox: [number, number, number, number],
    channels: string[] | null,
    orientation: TextMeasureOrientation = "upright",
    signal?: AbortSignal,
    publicViewToken?: string | null,
  ): Promise<TextMeasureInkResult> {
    const rota = toolsRoute(publicViewToken);
    return apiFetch(`${rota.base}/text-measure/ink`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        render_id: renderId,
        page,
        bbox: bbox.map((value) => Math.round(value)),
        channels: channels && channels.length > 0 ? channels : null,
        orientation,
      }),
      signal,
    }, rota.options).then((r) => jsonOrThrow<TextMeasureInkResult>(r));
  },

  /**
   * «Medir Texto», a fonte provável do texto em curva (F-MT.4): o contorno de
   * cada glifo casado com os glifos das fontes do servidor. Pelos índices dos
   * glifos (o balão «Curva») ou pela seleção (o «Ler Texto», que deixa o
   * servidor decidir o que há sob ela: texto vivo, curva ou nada). **Só com
   * sessão** — não há rota pelo token: `fontIdentificationAvailable` diz
   * quando a tela pode pedir.
   */
  identifyTextMeasureFont(
    renderId: string,
    page: number,
    query: TextMeasureFontQuery,
    signal?: AbortSignal,
  ): Promise<TextMeasureFontResult> {
    const body =
      "glyphs" in query
        ? { render_id: renderId, page, glyphs: query.glyphs, orientation: query.orientation }
        : { render_id: renderId, page, bbox: query.bbox.map((value) => Math.round(value)) };
    return apiFetch(`${API_BASE}/tools/text-measure/font`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    }).then((r) => jsonOrThrow<TextMeasureFontResult>(r));
  },

  measureScreenRegion(
    renderId: string,
    bbox: [number, number, number, number],
    channels?: string[] | null,
    signal?: AbortSignal,
    publicViewToken?: string | null,
  ): Promise<ScreenMeasureResult> {
    // O link público ou a sessão, e não `toolsRoute`: o link exportado do
    // compare não tem a rota, e a medição não existe no par.
    const url = publicViewToken
      ? `${API_BASE}/public-views/${encodeURIComponent(publicViewToken)}/tools/screen-measure`
      : `${API_BASE}/tools/screen-measure`;
    return apiFetch(
      url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          render_id: renderId,
          bbox: bbox.map((value) => Math.round(value)),
          channels: channels && channels.length > 0 ? channels : null,
        }),
        signal,
      },
      publicViewToken ? { public: true } : undefined,
    ).then((r) => jsonOrThrow<ScreenMeasureResult>(r));
  },

  sampleColorPicker(
    renderId: string,
    page: number,
    xPx: number,
    yPx: number,
    visibleSlugs: string[],
    publicViewToken?: string | null,
  ): Promise<ColorPickerResult> {
    const rota = toolsRoute(publicViewToken);
    return apiFetch(`${rota.base}/color-picker`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        render_id: renderId,
        page,
        x_px: xPx,
        y_px: yPx,
        visible_slugs: visibleSlugs,
      }),
    }, rota.options).then((r) => jsonOrThrow<ColorPickerResult>(r));
  },

  /**
   * Request a coverage-map WebP from the Viewer API v1 render cache scoped to
   * the given visible channel names. The returned Blob is decoded by
   * CoverageFilterOverlay into the native canvas overlay.
   */
  getCoverageMap(
    renderId: string,
    page: number,
    visibleSlugs: string[],
    signal?: AbortSignal,
    publicViewToken?: string | null,
  ): Promise<Blob> {
    const publicMode = Boolean(publicViewToken);
    return apiFetch(
      publicMode
        ? `${API_BASE}/public-views/${publicViewToken}/tools/coverage-map`
        : `${API_BASE}/tools/coverage-map`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          render_id: renderId,
          page,
          visible_slugs: visibleSlugs,
        }),
        signal,
      },
      publicMode ? { public: true } : undefined,
    ).then((r) => {
      if (!r.ok) throw new Error(`coverage-map ${r.status}`);
      return r.blob();
    });
  },

  // ---------- Queue (F1.18) ----------

  listQueueJobs(
    params?: QueueListParams,
    signal?: AbortSignal,
  ): Promise<QueueJobListResponse> {
    const p = new URLSearchParams();
    if (params?.status?.length) {
      for (const s of params.status) p.append("status", s);
    }
    if (params?.type) p.set("type", params.type);
    if (params?.file_id) p.set("file_id", params.file_id);
    if (params?.from) p.set("from", params.from);
    if (params?.to) p.set("to", params.to);
    if (params?.q) p.set("q", params.q);
    if (params?.priority !== undefined) p.set("priority", String(params.priority));
    if (params?.offset !== undefined) p.set("offset", String(params.offset));
    if (params?.limit !== undefined) p.set("limit", String(params.limit));
    const qs = p.toString();
    return apiFetch(
      `${API_BASE}/jobs${qs ? `?${qs}` : ""}`,
      { signal },
      { timeoutMs: POLL_TIMEOUT_MS },
    ).then((r) => jsonOrThrow<QueueJobListResponse>(r));
  },

  // ---------- Dicionário ortográfico (out/2026) ----------

  listSpellingDictionary(signal?: AbortSignal): Promise<DictionaryList> {
    return apiFetch(`${API_BASE}/admin/spelling-dictionary`, { signal }).then((r) =>
      jsonOrThrow<DictionaryList>(r),
    );
  },

  addToSpellingDictionary(body: DictionaryAddRequest): Promise<DictionaryAddResult> {
    return apiFetch(`${API_BASE}/admin/spelling-dictionary`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<DictionaryAddResult>(r));
  },

  removeFromSpellingDictionary(id: string): Promise<DictionaryEntry> {
    return apiFetch(`${API_BASE}/admin/spelling-dictionary/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }).then((r) => jsonOrThrow<DictionaryEntry>(r));
  },

  /**
   * O «aceitar» do cliente pelo link (21/10/2026): grava no dicionário da
   * empresa do link. Só existe com `tools.dictionary`; remover é do gerenciador.
   */
  acceptWordInPublicView(
    token: string,
    body: { language: DictionaryLanguage; word: string },
  ): Promise<{ added: Array<{ id: string; word: string; language: string }>; skipped: DictionarySkipped[] }> {
    return apiFetch(
      `${API_BASE}/public-views/${encodeURIComponent(token)}/dictionary`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
      { public: true },
    ).then((r) => jsonOrThrow(r));
  },

  // ---------- Organizações e uso dos recursos (21/10/2026) ----------

  listOrganizations(signal?: AbortSignal): Promise<{ items: OrganizationSummary[] }> {
    return apiFetch(`${API_BASE}/admin/organizations`, { signal }).then((r) =>
      jsonOrThrow<{ items: OrganizationSummary[] }>(r),
    );
  },

  getUsagePanel(params: UsagePanelParams, signal?: AbortSignal): Promise<UsagePanel> {
    const p = new URLSearchParams({ days: String(params.days) });
    if (params.surface) p.set("surface", params.surface);
    if (params.plan) p.set("plan", params.plan);
    if (params.organization_id !== undefined) p.set("organization_id", params.organization_id);
    if (params.viewer_kind) p.set("viewer_kind", params.viewer_kind);
    if (params.include_internal) p.set("include_internal", "true");
    return apiFetch(`${API_BASE}/admin/usage?${p.toString()}`, { signal }).then((r) => jsonOrThrow<UsagePanel>(r));
  },

  /**
   * Um pacote de uso. `keepalive` deixa o último pacote sair com a página
   * fechando. Falha é silenciosa: contar uso nunca atrapalha quem usa.
   */
  async sendUsage(target: UsageTarget, events: UsageEvent[], keepalive = false): Promise<void> {
    const corpo =
      target.kind === "public"
        ? { events }
        : target.kind === "shared"
          ? { opening_id: target.openingId, viewer_kind: target.viewerKind, events }
          : { opening_id: target.openingId, scope: target.scope, viewer_kind: target.viewerKind, events };
    const url =
      target.kind === "public"
        ? `${API_BASE}/public-views/${encodeURIComponent(target.token)}/usage`
        : target.kind === "shared"
          ? `${API_BASE}/viewer/v1/shared-views/${encodeURIComponent(target.token)}/usage`
          : `${API_BASE}/usage`;
    await apiFetch(
      url,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(corpo), keepalive },
      { public: target.kind !== "session" },
    );
  },

  // ---------- Testes do Viewer Pro (out/2026) ----------

  listProTrials(signal?: AbortSignal): Promise<ProTrialList> {
    return apiFetch(`${API_BASE}/admin/pro-trials`, { signal }).then((r) => jsonOrThrow<ProTrialList>(r));
  },

  setProTrialDays(id: string, days: number): Promise<ProTrial> {
    return apiFetch(`${API_BASE}/admin/pro-trials/${encodeURIComponent(id)}/days`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ days }),
    }).then((r) => jsonOrThrow<ProTrial>(r));
  },

  endProTrial(id: string): Promise<ProTrial> {
    return apiFetch(`${API_BASE}/admin/pro-trials/${encodeURIComponent(id)}/end`, { method: "POST" }).then((r) =>
      jsonOrThrow<ProTrial>(r),
    );
  },

  deleteProTrial(id: string): Promise<ProTrial> {
    return apiFetch(`${API_BASE}/admin/pro-trials/${encodeURIComponent(id)}`, { method: "DELETE" }).then((r) =>
      jsonOrThrow<ProTrial>(r),
    );
  },

  // ---------- Auditoria de acessos (set/2026) ----------

  listAuditLog(
    params?: AuditLogParams,
    signal?: AbortSignal,
  ): Promise<AuditLogListResponse> {
    const p = new URLSearchParams();
    if (params?.from) p.set("from", params.from);
    if (params?.to) p.set("to", params.to);
    if (params?.q) p.set("q", params.q);
    if (params?.kind) p.set("kind", params.kind);
    if (params?.viewer_kind) p.set("viewer_kind", params.viewer_kind);
    if (params?.organization_id !== undefined) p.set("organization_id", params.organization_id);
    if (params?.offset !== undefined) p.set("offset", String(params.offset));
    if (params?.limit !== undefined) p.set("limit", String(params.limit));
    const qs = p.toString();
    return apiFetch(`${API_BASE}/admin/audit-log${qs ? `?${qs}` : ""}`, { signal }).then((r) =>
      jsonOrThrow<AuditLogListResponse>(r),
    );
  },

  getQueueSummary(signal?: AbortSignal): Promise<QueueSummary> {
    return apiFetch(
      `${API_BASE}/jobs/summary`,
      { signal },
      { timeoutMs: POLL_TIMEOUT_MS },
    ).then((r) => jsonOrThrow<QueueSummary>(r));
  },

  getQueueJobDiagnostics(id: string, signal?: AbortSignal): Promise<QueueJobDiagnostics> {
    return apiFetch(`${API_BASE}/jobs/${id}/diagnostics`, { signal }).then((r) =>
      jsonOrThrow<QueueJobDiagnostics>(r),
    );
  },

  retryJob(id: string): Promise<QueueJob> {
    return apiFetch(`${API_BASE}/jobs/${id}/retry`, { method: "POST" }).then((r) =>
      jsonOrThrow<QueueJob>(r),
    );
  },

  cancelJob(id: string): Promise<QueueJob> {
    return apiFetch(`${API_BASE}/jobs/${id}/cancel`, { method: "POST" }).then((r) =>
      jsonOrThrow<QueueJob>(r),
    );
  },

  updateJobPriority(id: string, priority: number): Promise<QueueJob> {
    return apiFetch(`${API_BASE}/jobs/${id}/priority`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ priority }),
    }).then((r) => jsonOrThrow<QueueJob>(r));
  },

  getBarcodeTaskStatus(taskId: string): Promise<{
    status: string;
    result?: {
      ok: boolean;
      barcodes: import("@/state/barcodeStore").BarcodeResult[];
      reason?: string;
    };
    error?: string;
  }> {
    const rota = toolsRoute();
    return apiFetch(`${rota.base}/barcode/${taskId}`, undefined, rota.options).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: {
          ok: boolean;
          barcodes: import("@/state/barcodeStore").BarcodeResult[];
          reason?: string;
        };
        error?: string;
      }>(r),
    );
  },

  getPublicViewBarcodeTaskStatus(publicViewToken: string, taskId: string): Promise<{
    status: string;
    result?: {
      ok: boolean;
      barcodes: import("@/state/barcodeStore").BarcodeResult[];
      reason?: string;
    };
    error?: string;
  }> {
    return apiFetch(`${API_BASE}/public-views/${publicViewToken}/tools/barcode/${taskId}`, undefined, { public: true }).then((r) =>
      jsonOrThrow<{
        status: string;
        result?: {
          ok: boolean;
          barcodes: import("@/state/barcodeStore").BarcodeResult[];
          reason?: string;
        };
        error?: string;
      }>(r),
    );
  },

  // ---------- Insights (F1.20) ----------

  getInsightsOverview(range: string): Promise<InsightOverview> {
    const params = new URLSearchParams({ range });
    return apiFetch(`${API_BASE}/insights/overview?${params}`).then((r) =>
      jsonOrThrow<InsightOverview>(r),
    );
  },

  getInsightsTimeseries(metric: string, range: string): Promise<InsightsTimeseries> {
    const params = new URLSearchParams({ metric, range });
    return apiFetch(`${API_BASE}/insights/timeseries?${params}`).then((r) =>
      jsonOrThrow<InsightsTimeseries>(r),
    );
  },

  getInsightsOcr(range: string): Promise<OcrUsageSummary> {
    const params = new URLSearchParams({ range });
    return apiFetch(`${API_BASE}/insights/ocr?${params}`).then((r) =>
      jsonOrThrow<OcrUsageSummary>(r),
    );
  },

  getInsightsTranslation(range: string): Promise<TranslationUsageSummary> {
    const params = new URLSearchParams({ range });
    return apiFetch(`${API_BASE}/insights/translation?${params}`).then((r) =>
      jsonOrThrow<TranslationUsageSummary>(r),
    );
  },

  runInsightsSnapshot(): Promise<InsightsSnapshotRun> {
    const params = new URLSearchParams({ force: "true", sync: "true" });
    return apiFetch(`${API_BASE}/insights/snapshots/run?${params}`, {
      method: "POST",
    }).then((r) => jsonOrThrow<InsightsSnapshotRun>(r));
  },

  // ---------- Admin usuários ----------
  listUsers(): Promise<UserOut[]> {
    return apiFetch(`${API_BASE}/users`).then((r) => jsonOrThrow<UserOut[]>(r));
  },

  createUser(body: UserCreateIn): Promise<UserOut> {
    return apiFetch(`${API_BASE}/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<UserOut>(r));
  },

  updateUser(id: string, body: UserUpdateIn): Promise<UserOut> {
    return apiFetch(`${API_BASE}/users/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<UserOut>(r));
  },

  resetUserPassword(id: string, body: ResetPasswordIn): Promise<{ message: string }> {
    return apiFetch(`${API_BASE}/users/${id}/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<{ message: string }>(r));
  },

  listUserApiKeys(userId: string): Promise<ApiKeyOut[]> {
    return apiFetch(`${API_BASE}/users/${userId}/api-keys`).then((r) =>
      jsonOrThrow<ApiKeyOut[]>(r),
    );
  },

  createUserApiKey(userId: string, body: ApiKeyCreateIn): Promise<ApiKeyCreateOut> {
    return apiFetch(`${API_BASE}/users/${userId}/api-keys`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => jsonOrThrow<ApiKeyCreateOut>(r));
  },

  revokeUserApiKey(userId: string, keyId: string): Promise<ApiKeyOut> {
    return apiFetch(`${API_BASE}/users/${userId}/api-keys/${keyId}/revoke`, {
      method: "POST",
    }).then((r) => jsonOrThrow<ApiKeyOut>(r));
  },
};
